/* ===========================================================
   EditingPage.jsx – COMPLETE FILE 2025 06 09
   • Headline starts centered and can be dragged anywhere
   • Inline AI edit panel for image transformations
   • Photo swap mode for face/subject replacement
   ========================================================== */

   import React, { useState, useEffect, useRef } from "react";
   import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    ChevronDown,
    Type,
    RotateCcw,
    Eye,
    ThumbsUp,
    Edit2,
    Wand2,
    UserPlus,
    Calendar,
    Maximize2,
    Trash2,
    Undo2,
    Redo2,
    Upload,
    X,
    RefreshCw,
  } from "lucide-react";
   
   import { useAuthContext } from "../contexts/AuthContext";
   import { useError } from "../contexts/ErrorContext";
   import SaveDraftModal from "./ui/SaveDraftModal";
   import usePageTitle from "../hooks/usePageTitle";
   import Lottie from "lottie-react";
   import loadingAnimation from "../assets/lottie/loading.json";
   
import {
    generateMessage,
    getCardDetails,
    getSubscriptionStatus,
    inviteContributor,
    scheduleCardSend,
    saveCardDraft,
    dalleEdit,
    photoSwap,
    API_URL,
  } from "../utils/api";
   
   import { applyDalleEdit } from "../utils/aiEdit";
import GreetingCardCanvas from "./GreetingCard/GreetingCardCanvas";
import VideoCanvas from "./GreetingCard/VideoCanvas";
import EditableText from "./GreetingCard/EditableText";
import { Stage, Layer } from "react-konva";
   import PreviewModal from "./PreviewModal";
   import FontPicker from "./ui/FontPicker";
   import Button from "./ui/Button";
   import { useAutoSaveCard } from "../hooks/useAutoSaveCard";
   import { CARD_W as DEFAULT_W } from "../utils/geometry";
   import fbPixel from "../utils/fbPixel";
   
   /* ===========================================================
      helpers
      ========================================================== */
   const parseWidth = (s) => {
     const m = /^(\d+)x\d+$/i.exec(s || "");
     return m ? +m[1] : null;
   };
   
   const parseHeight = (s) => {
     const m = /^\d+x(\d+)$/i.exec(s || "");
     return m ? +m[1] : null;
   };
   
   const measure = (() => {
     const canvas = document.createElement("canvas");
     const ctx = canvas.getContext("2d");
     return (txt, font) => {
       ctx.font = font;
       return ctx.measureText(txt).width;
     };
   })();
   
   /* Helper function to load an image and return a promise */
   const loadImage = (url) => {
     return new Promise((resolve, reject) => {
       const img = new Image();
       img.crossOrigin = "anonymous";  // Enable CORS for cross-origin images
       img.onload = () => resolve(img);
       img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
       img.src = url;
     });
   };
   
   /* ===========================================================
      Main component
      ========================================================== */
   export default function EditingPage() {
  const { user } = useAuthContext();
  const { showError, showSuccess } = useError();
  const uid = user?.uid || "";

  const { category = "Graduation", id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};
  
  // Debug navigation state on load
  console.log("[EditingPage] Component loaded with:", {
    category,
    id,
    pathname: location.pathname,
    navState,
    hasImageSrc: !!navState.src,
    hasFrontText: !!navState.frontText,
    hasInsideText: !!navState.insideText
  });
  
  usePageTitle('Create Card');
   
     /* Scroll to top when component mounts */
     useEffect(() => {
       window.scrollTo(0, 0);
     }, []);
   
    /* geometry - parse both width and height from cardSize string (e.g., "1024x1024") */
    /* Make these state variables so they can be restored from saved card data */
    const [cardWidth, setCardWidth] = useState(parseWidth(navState.cardSize) || DEFAULT_W);
    const [cardHeight, setCardHeight] = useState(parseHeight(navState.cardSize) || (parseWidth(navState.cardSize) || DEFAULT_W) * 0.75);
   
       /* core state */
  const [cardId, setCardId] = useState(id && id.length > 10 ? id : null);
  const [title, setTitle] = useState(navState.title || "");
  const [frontText, setFrontText] = useState(navState.frontText || "");
  const [insidePages, setInsidePages] = useState(navState.insidePages || [navState.insideText || ""]);
  const [currentInsidePage, setCurrentInsidePage] = useState(0);
    const [tab, setTab] = useState("cover"); // Single tab system: cover/inside
    
    // Track if dimensions were restored from saved card (prevents image load from overwriting)
    const dimensionsRestoredRef = useRef(false);

  /* front-of-card - supports both image and video */
  const initialSrc = navState.src
    ? navState.src
    : `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/templates/${category}/${category.toLowerCase()}${id}.png`;
  const [imageUrl, setImageUrl] = useState(navState.isVideo ? "" : initialSrc);
  const [videoUrl, setVideoUrl] = useState(navState.isVideo ? initialSrc : "");
  const [mediaType, setMediaType] = useState(navState.mediaType || (navState.isVideo ? "video" : "image"));
  
  /* Track actual image dimensions for proper aspect ratio */
  const [imageDimensions, setImageDimensions] = useState({ width: cardWidth, height: cardHeight });
     const [layout, setLayout] = useState({
       x: navState.layout?.x, // Let GreetingCardCanvas handle initial positioning if not provided
       y: navState.layout?.y, // Let GreetingCardCanvas handle initial positioning if not provided
       width: navState.layout?.width || cardWidth * 0.8,
       height: navState.layout?.height || cardHeight * 0.6,
       padding: navState.layout?.padding || 40,
       align: navState.layout?.align || "center",
       fontSize: navState.layout?.fontSize || 72,
       fontFamily: navState.layout?.fontFamily || "Lilita One",
       fontWeight: navState.layout?.fontWeight || 700,
       lineHeight: navState.layout?.lineHeight || 1.2,
       letterSpacing: navState.layout?.letterSpacing || 1,
       fill: navState.layout?.fill || "#000000",
       stroke: navState.layout?.stroke || "",
       strokeWidth: navState.layout?.strokeWidth || 0,
       rotation: navState.layout?.rotation || 0,
       animStyle: navState.layout?.animStyle || "none",
       text: navState.layout?.text || navState.frontText || "",
       transform: navState.layout?.transform || "none"
     });
   
          /* inside-of-card */
     const [insideLayout, setInsideLayout] = useState(navState.inside_layout || {
       x: cardWidth * 0.1,
       y: cardHeight * 0.1,
       width: cardWidth * 0.8,
       height: cardHeight * 0.8,
       rotation: 0,
       scaleX: 1,
       scaleY: 1,
     });

     const [insideStyle, setInsideStyle] = useState(navState.inside_style || {
       fontFamily: "'Inter', sans-serif",
       fontSize: 24,
       fill: "#000000"
     });

    /* inside text styling state - FIXED: These were missing! */
    const [insideFont, setInsideFont] = useState(navState.inside_style?.fontFamily || "'Inter', sans-serif");
    const [insideFontSize, setInsideFontSize] = useState(navState.inside_style?.fontSize || 24);
    const [insideFill, setInsideFill] = useState(navState.inside_style?.fill || "#000000");
    
    /* inside card design - decorative border */
    const [insideBorderColor, setInsideBorderColor] = useState(navState.inside_style?.borderColor || "#d4af37");
    const [insideBorderWidth, setInsideBorderWidth] = useState(navState.inside_style?.borderWidth ?? 9);
    const [insideBorderStyle, setInsideBorderStyle] = useState(navState.inside_style?.borderStyle || "solid");
    
    /* cover/front card design - decorative border */
    const [coverBorderColor, setCoverBorderColor] = useState(navState.layout?.borderColor || "");
    const [coverBorderWidth, setCoverBorderWidth] = useState(navState.layout?.borderWidth || 0);
    const [coverBorderStyle, setCoverBorderStyle] = useState(navState.layout?.borderStyle || "solid");
    
    /* cover decorative overlay (floral corners, wreaths, snow, etc.) */
    const [coverOverlay, setCoverOverlay] = useState(navState.layout?.overlay || "none");
    const [coverOverlayColor, setCoverOverlayColor] = useState(navState.layout?.overlayColor || "#1F2937");
   
     /* signatures */
     const [signatures, setSignatures] = useState(navState.signatures || []);
   
     /* collaborator text entries - multiple independent text boxes from collaborators */
     const [insideTextEntries, setInsideTextEntries] = useState(navState.inside_text_entries || []);
   
     /* sharing and schedule */
     const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
     const [sloganTone, setSloganTone] = useState("Funny");
     const [sloganRecipient, setSloganRecipient] = useState(
       navState.recipient || "Friend"
     );
     const [sloganOccasion, setSloganOccasion] = useState(category);
     const [loadingSlogan, setLoadingSlogan] = useState(false);
       const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [inviteError, setInviteError] = useState("");
     const [sendDate, setSendDate] = useState("");
     const [scheduleLoading, setScheduleLoading] = useState(false);
     const [scheduleSuccess, setScheduleSuccess] = useState("");
     const [scheduleError, setScheduleError] = useState("");
     const [showPreview, setShowPreview] = useState(false);
     const [saving, setSaving] = useState(false);
     const [showSaveModal, setShowSaveModal] = useState(false);
       const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
     const [recipientEmail, setRecipientEmail] = useState("");
     const [recipientName, setRecipientName] = useState("");
     const [senderName, setSenderName] = useState("");
   
   /* AI edit panel state */
   const [aiPanelOpen, setAiPanelOpen] = useState(false);
   const [prompt, setPrompt] = useState("");
   const [aiLoading, setAiLoading] = useState(false);
   const [aiError, setAiError] = useState("");
   const [aiProgress, setAiProgress] = useState({ stage: "", percent: 0 });
   
   /* Photo swap state */
   const [photoSwapMode, setPhotoSwapMode] = useState(false);
   const [referenceImage, setReferenceImage] = useState(null);
   const [referencePreview, setReferencePreview] = useState(null);
   const [isDraggingRef, setIsDraggingRef] = useState(false);
   const referenceInputRef = useRef(null);
   
   /* Undo/Redo history for ALL card changes (images, text, styles, etc.) */
   const [stateHistory, setStateHistory] = useState([]);
   const [historyIndex, setHistoryIndex] = useState(-1);
   const isRestoringRef = useRef(false); // Prevent history capture during restore
   
   // Use refs to track latest state for undo/redo to avoid stale closures
   const stateHistoryRef = useRef(stateHistory);
   const historyIndexRef = useRef(historyIndex);
   
   // Keep refs in sync with state
   useEffect(() => {
     stateHistoryRef.current = stateHistory;
     historyIndexRef.current = historyIndex;
   }, [stateHistory, historyIndex]);
   
   const canUndo = historyIndex > 0;
   const canRedo = historyIndex < stateHistory.length - 1;
   
     /* Error tracking and user feedback */
     const [userFeedback, setUserFeedback] = useState("");
     const [showFeedback, setShowFeedback] = useState(false);
   
   /* Subscription limits for feature gating (can_send, can_collaborate, etc.) */
   const [subscriptionLimits, setSubscriptionLimits] = useState(null);
   
        /* Initialize sender name from user auth context */
   useEffect(() => {
     if (user && !senderName) {
       setSenderName(user.displayName || user.email?.split('@')[0] || "");
     }
   }, [user, senderName]);

  /* Check subscription status and store limits for feature gating */
  useEffect(() => {
    // Check subscription whenever user is authenticated
    if (user) {
      const checkSubscription = async () => {
        try {
          const { data: statusData } = await getSubscriptionStatus();
          
          // Store subscription limits for feature gating (can_send, can_collaborate, etc.)
          if (statusData.limits) {
            setSubscriptionLimits(statusData.limits);
            console.log('[Subscription Check] Loaded limits:', statusData.limits);
          }
          
          // Preview tier users (no active subscription) are allowed to create/edit cards
          // They just can't send or collaborate - those will be gated in the handlers
          // No redirect needed anymore - let them experience the product!
          
        } catch (err) {
          console.error('[Subscription Check] Failed to verify subscription:', err);
          // On error, set restrictive limits (Preview mode)
          setSubscriptionLimits({
            can_send: false,
            can_collaborate: false,
            can_schedule: false
          });
        }
      };
      
      checkSubscription();
    }
  }, [user]);

   /* Handle template data from session storage (post-subscription flow) */
   useEffect(() => {
     const pendingTemplateData = sessionStorage.getItem('pendingTemplateData');
     if (pendingTemplateData && !navState.src) {
       try {
         const templateData = JSON.parse(pendingTemplateData);
         console.log('[Template Recovery] Loading template after subscription:', templateData);
         
         // Handle video vs image templates
         if (templateData.isVideo || templateData.mediaType === "video") {
           console.log('[Template Recovery] Setting video template:', templateData.src);
           setVideoUrl(templateData.src);
           setImageUrl(""); // Clear image URL for video templates
           setMediaType("video");
         } else {
           console.log('[Template Recovery] Setting image template:', templateData.src);
           setImageUrl(templateData.src);
           setVideoUrl("");
           setMediaType("image");
         }
         
         // Also update the URL to match the correct template if needed
         if (templateData.category && templateData.id) {
           const expectedPath = `/edit/${templateData.category}/${templateData.id}`;
           const currentPath = window.location.pathname;
           if (currentPath !== expectedPath) {
             console.log('[Template Recovery] Correcting URL to match template:', expectedPath);
             navigate(expectedPath, { 
               replace: true,
               state: {
                 src: templateData.src,
                 category: templateData.category,
                 cardSize: templateData.cardSize || "1024x1024",
                 isVideo: templateData.isVideo || false,
                 mediaType: templateData.mediaType || "image",
                 firebaseUrl: templateData.firebaseUrl || null,
                 fromSubscription: true
               }
             });
           }
         }
         
        // Clear the session storage after use
        sessionStorage.removeItem('pendingTemplateData');
      } catch (err) {
        console.error('Failed to parse pending template data:', err);
        sessionStorage.removeItem('pendingTemplateData');
      }
    }
  }, [navState.src, navigate]);

  /* Track actual image dimensions for proper canvas/container sizing */
  useEffect(() => {
    if (!imageUrl) return;
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      console.log('[EditingPage] Image loaded with dimensions:', img.width, 'x', img.height);
      // Skip updating if dimensions were already restored from saved card_size
      // This prevents the image load from overwriting the saved dimensions
      if (dimensionsRestoredRef.current) {
        console.log('[EditingPage] Skipping dimension update - using restored card_size dimensions');
        return;
      }
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      console.warn('[EditingPage] Failed to load image for dimension tracking:', imageUrl);
      // Keep default dimensions on error
    };
    img.src = imageUrl;
  }, [imageUrl]);

      /* Track changes to mark card as unsaved - FIXED: Only track content changes */
  useEffect(() => {
    // Only mark as unsaved if we have a previous save timestamp and we're currently in saved state
    if (lastSavedAt && saveStatus === 'saved' && !saving) {
      console.log("[Change Detection] Marking card as unsaved due to content changes");
      setSaveStatus('unsaved');
      setHasUnsavedChanges(true);
    }
  }, [
    frontText, 
    insidePages, 
    layout, 
    insideLayout, 
    insideFont,        // ADDED: Missing inside text styling
    insideFontSize,    // ADDED: Missing inside text styling  
    insideFill,        // ADDED: Missing inside text styling
    imageUrl, 
    signatures,        // This was already included
    currentInsidePage, // ADDED: Track current page changes
    // NOTE: Removed lastSavedAt, saveStatus, saving from dependencies
    // as these are control variables, not content that should trigger unsaved state
  ]);
   
     /* Shared function to update recipient name in both sections */
     const updateRecipientName = (newName) => {
       setRecipientName(newName);
     };
   
     /* Performance monitoring */
     useEffect(() => {
       const handleError = (event) => {
         console.error('Unhandled error:', event.error);
         setUserFeedback("Something went wrong. Please try refreshing the page.");
         setShowFeedback(true);
       };
   
       window.addEventListener('error', handleError);
       return () => window.removeEventListener('error', handleError);
     }, []);
   
   /* ===========================================================
      Fetch card details when editing an existing draft
      ========================================================== */
   useEffect(() => {
     // Reset dimension restored flag when loading a different card
     dimensionsRestoredRef.current = false;
     
     // Always fetch card details if we have a cardId and navState doesn't have complete inside_style
     // This ensures that even when navigating with navState.src, we still load saved inside text styling
     const needsFetch = cardId && (!navState.inside_style || !navState.src || !navState.frontText);
     
     if (needsFetch) {
       const timer = setTimeout(() => {
         getCardDetails(cardId)
             .then(({ data }) => {
            const {
                title: savedTitle,
                front_text,
                inside_text,
                inside_text_entries: savedTextEntries, // Collaborator text entries
                image_url,
                video_url: savedVideoUrl,     // Video URL for video greeting cards
                media_type: savedMediaType,   // "image" or "video" to distinguish card type
                layout: savedLayout,
                signatures: savedSig,
                inside_layout: savedInsideLayout,
                inside_style: savedInsideStyle,
                card_size: savedCardSize
              } = data;
   
              // Restore video URL and media type for video cards
              if (savedVideoUrl) {
                console.log("[Card Loading] Restoring video card:", { savedVideoUrl, savedMediaType });
                setVideoUrl(savedVideoUrl);
                setMediaType("video");
                // For video cards, image_url might be empty or be a thumbnail
                setImageUrl(image_url || "");
              } else {
                setImageUrl(image_url || "");
                setVideoUrl("");
                setMediaType(savedMediaType || "image");
              }
              setFrontText(front_text || "");
              
              // Load owner's inside text (always load this - it's the owner's own message)
              const pages = inside_text ? inside_text.split("\n").map(t => t.trim()).filter(Boolean) : [""];
              setInsidePages(pages.length > 0 ? pages : [""]);
              
              // Load collaborator text entries separately (these are in addition to owner's text)
              if (savedTextEntries && Array.isArray(savedTextEntries) && savedTextEntries.length > 0) {
                setInsideTextEntries(savedTextEntries);
                console.log("[Card Loading] Loaded collaborator text entries:", savedTextEntries);
              }
              
              // Restore card dimensions from saved card_size
              if (savedCardSize) {
                const savedWidth = parseWidth(savedCardSize);
                const savedHeight = parseHeight(savedCardSize);
                if (savedWidth) {
                  setCardWidth(savedWidth);
                  setCardHeight(savedHeight || savedWidth * 0.75);
                  // Also update imageDimensions to ensure inside card matches cover
                  setImageDimensions({ width: savedWidth, height: savedHeight || savedWidth * 0.75 });
                  // Mark that dimensions were restored - prevents image load from overwriting
                  dimensionsRestoredRef.current = true;
                  console.log("[Card Loading] Restored card dimensions:", { savedCardSize, savedWidth, savedHeight });
                }
              }
             if (savedLayout) {
               // Only treat positions outside card bounds as needing auto-centering
               // Note: x=0, y=0 is a valid position (top-left), don't treat it as needing auto-center
               const isOutOfBounds = (
                 (typeof savedLayout.x === 'number' && (savedLayout.x < 0 || savedLayout.x > cardWidth)) ||
                 (typeof savedLayout.y === 'number' && (savedLayout.y < 0 || savedLayout.y > cardHeight))
               );
               if (isOutOfBounds) {
                 console.log("[Card Loading] Position was out of bounds, will auto-center:", {
                   savedX: savedLayout.x,
                   savedY: savedLayout.y,
                   cardWidth,
                   cardHeight
                 });
               }
               // Only auto-center if position is out of bounds or undefined
               // Valid x=0, y=0 positions should be preserved
               setLayout({
                 x: (typeof savedLayout.x === 'number' && !isOutOfBounds) ? savedLayout.x : undefined,
                 y: (typeof savedLayout.y === 'number' && !isOutOfBounds) ? savedLayout.y : undefined,
                   width: typeof savedLayout.width === 'number' ? savedLayout.width : cardWidth * 0.8,
                   height: typeof savedLayout.height === 'number' ? savedLayout.height : cardHeight * 0.6,
                   rotation: typeof savedLayout.rotation === 'number' ? savedLayout.rotation : 0,
                   scaleX: typeof savedLayout.scaleX === 'number' ? savedLayout.scaleX : 1,
                   scaleY: typeof savedLayout.scaleY === 'number' ? savedLayout.scaleY : 1,
                   padding: savedLayout.padding || 40,
                   align: savedLayout.align || "center",
                   fontSize: savedLayout.fontSize || 72,
                   fontFamily: savedLayout.fontFamily || "Lilita One",
                   fontWeight: savedLayout.fontWeight || 700,
                   lineHeight: savedLayout.lineHeight || 1.2,
                   letterSpacing: savedLayout.letterSpacing || 1,
                   fill: savedLayout.fill || "#000000",
                   stroke: savedLayout.stroke || "",
                   strokeWidth: savedLayout.strokeWidth || 0,
                   animStyle: savedLayout.animStyle || "none",
                   text: savedLayout.text || front_text || "",
                   transform: savedLayout.transform || "none"
                 });
                 // Restore cover border settings
                 setCoverBorderColor(savedLayout.borderColor || "");
                 setCoverBorderWidth(savedLayout.borderWidth || 0);
                 setCoverBorderStyle(savedLayout.borderStyle || "solid");
                 // Restore cover overlay settings
                 setCoverOverlay(savedLayout.overlay || "none");
                 setCoverOverlayColor(savedLayout.overlayColor || "#1F2937");
               }
              if (savedInsideLayout) {
                console.log("[Card Loading] Restoring inside layout:", savedInsideLayout);
                setInsideLayout({
                  x: typeof savedInsideLayout.x === 'number' ? savedInsideLayout.x : undefined,
                  y: typeof savedInsideLayout.y === 'number' ? savedInsideLayout.y : undefined,
                  width: typeof savedInsideLayout.width === 'number' ? savedInsideLayout.width : cardWidth * 0.8,
                  height: typeof savedInsideLayout.height === 'number' ? savedInsideLayout.height : cardHeight * 0.8,
                  rotation: typeof savedInsideLayout.rotation === 'number' ? savedInsideLayout.rotation : 0,
                  scaleX: typeof savedInsideLayout.scaleX === 'number' ? savedInsideLayout.scaleX : 1,
                  scaleY: typeof savedInsideLayout.scaleY === 'number' ? savedInsideLayout.scaleY : 1,
                  align: savedInsideLayout.align || "left",
                  fontFamily: savedInsideLayout.fontFamily || "'Inter', sans-serif",
                  fontSize: savedInsideLayout.fontSize || 24,
                  fill: savedInsideLayout.fill || "#000000",
                  stroke: savedInsideLayout.stroke || "",
                  strokeWidth: savedInsideLayout.strokeWidth || 0,
                  animStyle: savedInsideLayout.animStyle || "none"
                });
              }
             if (savedInsideStyle) {
               console.log("[Card Loading] Restoring inside style:", savedInsideStyle);
               setInsideFont(savedInsideStyle.fontFamily || "'Inter', sans-serif");
               setInsideFontSize(savedInsideStyle.fontSize || 24);
               setInsideFill(savedInsideStyle.fill || "#000000");
               setInsideBorderColor(savedInsideStyle.borderColor || "");
               setInsideBorderWidth(savedInsideStyle.borderWidth || 0);
               setInsideBorderStyle(savedInsideStyle.borderStyle || "solid");
             }
              setSignatures(savedSig || []);
              
              console.log("[Card Loading] Card loaded successfully:", {
                front_text,
                inside_pages: pages,
                signatures: savedSig,
                inside_style: savedInsideStyle
              });
              
              // Only mark as saved if this is an existing card being loaded
              if (cardId) {
                console.log("[Card Loading] Existing card loaded, marking as saved");
                setSaveStatus('saved');
                setHasUnsavedChanges(false);
                setLastSavedAt(new Date());
              } else {
                console.log("[Card Loading] New card loaded, marking as unsaved");
                setSaveStatus('unsaved');
                setHasUnsavedChanges(true);
                setLastSavedAt(null);
              }
              
              // Also update the slogan fields if they exist in the saved data
              if (data.recipient) setSloganRecipient(data.recipient);
              if (data.occasion) setSloganOccasion(data.occasion);
              
              // Check if card has collaborators (invites) to enable polling
              if (data.invites && data.invites.length > 0) {
                setHasCollaborators(true);
                console.log("[Card Loading] Card has collaborators, enabling polling");
              }
            })
            .catch((err) => {
              if (err.response?.status !== 404) {
                console.error("Failed to fetch card details:", err);
              }
            });
         }, 1000);
   
         return () => clearTimeout(timer);
       }
     }, [cardId, navState.src, navState.frontText, navState.inside_style, cardWidth, cardHeight]);

  /* ===========================================================
     Poll for collaborator updates (only when card has collaborators)
     ========================================================== */
  const [hasCollaborators, setHasCollaborators] = useState(false);
  
  useEffect(() => {
    // Only poll if we have a cardId and the card has collaborators
    if (!cardId || !hasCollaborators) return;
    
    let isTabVisible = true;
    
    // Pause polling when tab is not visible to save resources
    const handleVisibilityChange = () => {
      isTabVisible = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const pollInterval = setInterval(async () => {
      // Skip if tab is not visible
      if (!isTabVisible) return;
      
      try {
        const { data } = await getCardDetails(cardId);
        
        // Update collaborator text entries if they've changed
        if (data.inside_text_entries && Array.isArray(data.inside_text_entries)) {
          setInsideTextEntries(prevEntries => {
            const prevHash = JSON.stringify(prevEntries);
            const newHash = JSON.stringify(data.inside_text_entries);
            
            if (prevHash !== newHash) {
              console.log("[Collaboration Poll] Updated text entries from server:", data.inside_text_entries);
              return data.inside_text_entries;
            }
            return prevEntries;
          });
        }
      } catch (err) {
        // Silently fail polling - don't interrupt user experience
        console.debug("[Collaboration Poll] Failed to fetch updates:", err.message);
      }
    }, 30000); // Poll every 30 seconds (less aggressive)
    
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cardId, hasCollaborators]);
   
     /* ===========================================================
        Template image if starting from template
        ========================================================== */
     useEffect(() => {
       if (navState.src) {
         setImageUrl(navState.src);
         // Mark as unsaved when loading a new template
         if (!cardId) {
           console.log("[Template Loading] New template loaded, marking as unsaved");
           setSaveStatus('unsaved');
           setHasUnsavedChanges(true);
           setLastSavedAt(null);
         }
       } else if (category && id) {
         const base =
           process.env.REACT_APP_API_URL || "http://localhost:8000";
         const templateUrl = `${base}/templates/${category.toLowerCase()}/${category.toLowerCase()}${id}.png`;
         setImageUrl(templateUrl);
         // Mark as unsaved when loading a new template
         if (!cardId) {
           console.log("[Template Loading] New template loaded, marking as unsaved");
           setSaveStatus('unsaved');
           setHasUnsavedChanges(true);
           setLastSavedAt(null);
         }
       }
     }, [navState.src, category, id, cardId]);
   
     /* ===========================================================
        Auto fit headline font size
        ========================================================== */
     const lastFit = useRef(0);
     useEffect(() => {
       const font = `${layout.fontWeight} ${layout.fontSize}px ${layout.fontFamily}`;
       const safeFrontText = String(frontText || "");
       const lines = safeFrontText.split("\n");
       const longest = Math.max(...lines.map((l) => measure(l || " ", font)));
       const totalH = lines.length * layout.fontSize * layout.lineHeight;
       const scale = Math.min(layout.width / longest, layout.height / totalH, 1);
       const target = Math.max(12, Math.floor(layout.fontSize * scale));
   
       if (Math.abs(target - layout.fontSize) >= 1 && target !== lastFit.current) {
         lastFit.current = target;
         setLayout((p) => ({ ...p, fontSize: target }));
       }
     }, [
       frontText,
       layout.width,
       layout.height,
       layout.fontSize,
       layout.fontFamily,
       layout.fontWeight,
       layout.lineHeight,
     ]);
   
    /* ===========================================================
       Auto save hook
       ========================================================== */
   // Validate position is within card bounds before saving (prevent saving off-screen positions)
   const validatePosition = (x, y) => {
     const maxX = cardWidth - 10; // Allow some margin
     const maxY = cardHeight - 10;
     const minX = 0;
     const minY = 0;
     return {
       x: typeof x === 'number' ? Math.max(minX, Math.min(maxX, Math.round(x))) : undefined,
       y: typeof y === 'number' ? Math.max(minY, Math.min(maxY, Math.round(y))) : undefined,
     };
   };
   
   const validatedPos = validatePosition(layout.x, layout.y);
   
   const cardState = {
     front_text: String(frontText || ""),
     inside_text: insidePages.join("\n"),
     image_url: imageUrl,
     video_url: videoUrl,           // NEW: Video URL for video greeting cards
     media_type: mediaType,         // NEW: "image" or "video"
     layout: {
       ...layout,
       // Keep x/y as undefined if not set - this allows auto-centering on reload
       // Also validate that positions are within card bounds
       x: validatedPos.x,
       y: validatedPos.y,
         width: Math.round(layout.width),
         height: Math.round(layout.height),
         padding: layout.padding || 0,
         align: layout.align || "center",
         fontSize: layout.fontSize || 72,
         fontFamily: layout.fontFamily || "Lilita One",
         fontWeight: layout.fontWeight || 700,
         lineHeight: layout.lineHeight || 1.2,
         letterSpacing: layout.letterSpacing || 1,
         fill: layout.fill || "#000000",
         stroke: layout.stroke || "",
         strokeWidth: layout.strokeWidth || 0,
         rotation: layout.rotation || 0,
         animStyle: layout.animStyle || "none",
         text: String(frontText || ""),
         transform: layout.transform || "none",
         borderColor: coverBorderColor || "",
         borderWidth: coverBorderWidth || 0,
         borderStyle: coverBorderStyle || "solid",
         overlay: coverOverlay || "none",
         overlayColor: coverOverlayColor || "#1F2937"
       },
      inside_layout: {
        ...insideLayout,
        x: insideLayout.x !== undefined ? Math.round(insideLayout.x) : cardWidth * 0.1,
        y: insideLayout.y !== undefined ? Math.round(insideLayout.y) : cardHeight * 0.1,
        width: Math.round(insideLayout.width),
        height: Math.round(insideLayout.height),
        align: insideLayout.align || "left",
        fontFamily: insideFont || "'Inter', sans-serif",
        fontSize: insideFontSize || 24,
        fill: insideFill || "#000000",
        stroke: insideLayout.stroke || "",
        strokeWidth: insideLayout.strokeWidth || 0,
        rotation: insideLayout.rotation || 0,
        animStyle: insideLayout.animStyle || "none",
      text: String(insidePages[currentInsidePage] || "")
      },
     inside_style: {
       fontFamily: insideFont || "'Inter', sans-serif",
       fontSize: insideFontSize || 24,
       fill: insideFill || "#000000",
       borderColor: insideBorderColor || "",
       borderWidth: insideBorderWidth || 0,
       borderStyle: insideBorderStyle || "solid"
     },
     signatures,
     // Collaborator text entries
     inside_text_entries: insideTextEntries,
      recipient: sloganRecipient,
      occasion: sloganOccasion,
      category,
      card_size: `${imageDimensions.width}x${imageDimensions.height}`,
    };
   
         /* Auto-save with comprehensive logging */
     useEffect(() => {
       console.log("[Auto-save] Card state being saved:", {
         cardId,
         front_text: String(frontText || ""),
         inside_text: insidePages.join("\n"),
         image_url: imageUrl,
         layout_summary: {
           x: layout.x,
           y: layout.y,
           fontSize: layout.fontSize,
           fontFamily: layout.fontFamily,
           text: String(frontText || "")
         },
        inside_layout_summary: {
          x: insideLayout.x,
          y: insideLayout.y,
          fontSize: insideFontSize,  // Using correct state variable
          fontFamily: insideFont,    // Using correct state variable
          fill: insideFill,          // Using correct state variable
         text: String(insidePages[currentInsidePage] || "")
       },
        inside_style: {
          fontFamily: insideFont,
          fontSize: insideFontSize,
          fill: insideFill,
          borderColor: insideBorderColor,
          borderWidth: insideBorderWidth,
          borderStyle: insideBorderStyle
        },
        signatures: signatures,
         insidePages_count: insidePages.length,
         currentInsidePage,
         hasChanges: hasUnsavedChanges
       });
     }, [cardState, cardId, frontText, insidePages, imageUrl, layout, insideLayout, insideFont, insideFontSize, insideFill, insideBorderColor, insideBorderWidth, insideBorderStyle, coverBorderColor, coverBorderWidth, coverBorderStyle, coverOverlay, coverOverlayColor, signatures, currentInsidePage, hasUnsavedChanges]);

     useAutoSaveCard(
       cardState, 
       cardId, 
       (newId) => {
         console.log("[Auto-save] New card ID assigned:", newId);
         setCardId(newId);
         navigate(`/edit/${category}/${newId}`, {
           replace: true,
           state: { ...navState, src: imageUrl },
         });
       },
       true, // enabled
       () => {
         // Auto-save completed successfully
         console.log("[Auto-save] Auto-save completed, updating save status");
         setSaveStatus('saved');
         setHasUnsavedChanges(false);
         setLastSavedAt(new Date());
       }
     );
   
     /* ===========================================================
        Helpers for inside pages
        ========================================================== */
     const updateInsidePage = (text) => {
       setInsidePages((pages) =>
         pages.map((p, i) => (i === currentInsidePage ? text : p))
       );
     };
   
     const handleAddPage = () => {
       setInsidePages((p) => [...p, ""]);
       setCurrentInsidePage(insidePages.length);
     };
   
     const handleRemovePage = (index) => {
       if (insidePages.length <= 1) return;
       setInsidePages((p) => p.filter((_, i) => i !== index));
       setCurrentInsidePage((prev) => Math.min(prev, insidePages.length - 2));
     };
   
     /* ===========================================================
        AI slogan generator
        ========================================================== */
     const generateAIHeadline = async () => {
       setLoadingSlogan(true);
       try {
         const fd = new FormData();
         fd.append("tone", sloganTone);
         fd.append("recipient", sloganRecipient);
         fd.append("occasion", sloganOccasion);
         const { data } = await generateMessage(fd);
         setFrontText(data.front_text);
       } finally {
         setLoadingSlogan(false);
       }
     };
   
     /* ===========================================================
        Manual save
        ========================================================== */
     const handleManualSave = () => setShowSaveModal(true);

     const handleSaveConfirm = async (cardTitle) => {
       setSaving(true);
       setSaveStatus('saving');
       try {
         console.log("[Manual Save] Starting save with current state:", {
           frontText: String(frontText || ""),
           insidePages,
           currentInsidePage,
           insideFont,
           insideFontSize, 
           insideFill,
           signatures,
           cardId
         });

        // Use the same payload structure as auto-save to ensure consistency
        // Only include card_id if we have a real Firestore ID (not AI-generated temp ID)
        const isRealId = cardId && !cardId.startsWith("ai_");
        const payload = {
          ...(isRealId ? { card_id: cardId } : {}),
          title: cardTitle || String(frontText || "").slice(0, 30) || "Untitled Card",
          front_text: String(frontText || "").trim(),
          inside_text: insidePages.map((p) => String(p || "").trim()).filter(Boolean).join("\n"),
          image_url: imageUrl || "",
          video_url: videoUrl || "",           // Video greeting card URL
          media_type: mediaType || "image",    // "image" or "video" to distinguish card type
          layout: {
            ...layout,
            // Keep x/y as undefined if not set - this allows auto-centering on reload
            x: typeof layout.x === 'number' ? Math.round(layout.x) : undefined,
            y: typeof layout.y === 'number' ? Math.round(layout.y) : undefined,
            width: Math.round(layout.width || cardWidth * 0.8),
            height: Math.round(layout.height || cardHeight * 0.6),
            rotation: typeof layout.rotation === 'number' ? layout.rotation : 0,
            scaleX: typeof layout.scaleX === 'number' ? layout.scaleX : 1,
            scaleY: typeof layout.scaleY === 'number' ? layout.scaleY : 1,
            padding: layout.padding || 40,
            align: layout.align || "center",
            fontSize: layout.fontSize || 72,
            fontFamily: layout.fontFamily || "Lilita One",
            fontWeight: layout.fontWeight || 700,
            lineHeight: layout.lineHeight || 1.2,
            letterSpacing: layout.letterSpacing || 1,
            fill: layout.fill || "#000000",
            stroke: layout.stroke || "",
            strokeWidth: layout.strokeWidth || 0,
            animStyle: layout.animStyle || "none",
            text: String(frontText || "").trim(),
            transform: layout.transform || "none",
            borderColor: coverBorderColor || "",
            borderWidth: coverBorderWidth || 0,
            borderStyle: coverBorderStyle || "solid",
            overlay: coverOverlay || "none",
            overlayColor: coverOverlayColor || "#1F2937"
          },
           inside_layout: {
             ...insideLayout,
             x: typeof insideLayout.x === 'number' ? Math.round(insideLayout.x) : cardWidth * 0.1,
             y: typeof insideLayout.y === 'number' ? Math.round(insideLayout.y) : cardHeight * 0.1,
             width: Math.round(insideLayout.width || cardWidth * 0.8),
             height: Math.round(insideLayout.height || cardHeight * 0.8),
             rotation: typeof insideLayout.rotation === 'number' ? insideLayout.rotation : 0,
             scaleX: typeof insideLayout.scaleX === 'number' ? insideLayout.scaleX : 1,
             scaleY: typeof insideLayout.scaleY === 'number' ? insideLayout.scaleY : 1,
             align: insideLayout.align || "left",
             fontFamily: insideFont || "'Inter', sans-serif",
             fontSize: insideFontSize || 24,
             fill: insideFill || "#000000",
             stroke: insideLayout.stroke || "",
             strokeWidth: insideLayout.strokeWidth || 0,
            animStyle: insideLayout.animStyle || "none",
            text: String(insidePages[currentInsidePage] || "").trim()
          },
          inside_style: {
            fontFamily: insideFont || "'Inter', sans-serif",
            fontSize: insideFontSize || 24,
            fill: insideFill || "#000000",
            borderColor: insideBorderColor || "",
            borderWidth: insideBorderWidth || 0,
            borderStyle: insideBorderStyle || "solid"
          },
          signatures: signatures.map((s) => String(s || "").trim()).filter(Boolean),
          // Collaborator text entries
          inside_text_entries: insideTextEntries,
          recipient: sloganRecipient || "",
          occasion: sloganOccasion || "",
          category: category || "",
          card_size: `${imageDimensions.width}x${imageDimensions.height}`,
          // Only set status to draft for new cards
          status: isRealId ? undefined : "draft"
        };

         // Don't save completely empty cards
         if (!payload.front_text.trim() && !payload.inside_text.trim() && !payload.title?.trim()) {
           setSaving(false);
           setSaveStatus('idle');
           setShowSaveModal(false);
           return;
         }

         console.log("[Manual Save] Payload:", payload);
         const newId = await saveCardDraft(payload);
         
         console.log("[Manual Save] Save successful:", {
           newId,
           wasNewCard: !cardId,
           savedFrontText: payload.front_text,
           savedInsideText: payload.inside_text,
           savedSignatures: payload.signatures,
           savedInsideStyle: payload.inside_style
         });
         
         // Update cardId if this was a new card or AI-generated temp ID
         if (!isRealId) {
           setCardId(newId);
           const newState = {
             ...navState,
             src: imageUrl,
             frontText,
             layout: {
               ...layout,
               text: frontText?.trim() || "",
             },
             insideText: insidePages[currentInsidePage],
             insideLayout: {
               ...insideLayout,
               text: insidePages[currentInsidePage]?.trim() || "",
             },
             cardSize: `${cardWidth}x${cardHeight}`,
           };
           navigate(`/edit/${category}/${newId}`, {
             replace: true,
             state: newState,
           });
         }
         
                 // Only mark as saved if the save actually succeeded
        setSaveStatus('saved');
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());
        setShowSaveModal(false);
         
      } catch (err) {
        console.error("Save error:", err.response?.data || err);
        setSaveStatus('error');
        
        // Check if this is a limit/upgrade error (402/403) - api.js interceptor already handles these
        const isLimitError = err.response?.status === 402 || err.response?.status === 403;
        
        // Only show error for non-limit errors (limit errors are handled by api.js interceptor)
        if (!isLimitError) {
          // Extract error message properly - backend may return string or object
          const errorDetail = err.response?.data?.detail;
          let errorMessage = "Failed to save draft. Please try again.";
          
          if (typeof errorDetail === 'string') {
            errorMessage = errorDetail;
          } else if (typeof errorDetail === 'object' && errorDetail?.message) {
            errorMessage = errorDetail.message;
          }
          
          showError(errorMessage, false);
        }
        // For limit errors (402/403), the api.js interceptor already shows the upgrade modal
      } finally {
         setSaving(false);
       }
     };
   
   /* ===========================================================
      Undo/Redo functions for ALL card changes
      ========================================================== */
   
   // Capture current state as a snapshot
   const captureStateSnapshot = () => {
     return {
       // Image/Video
       imageUrl,
       videoUrl,
       // Cover layout and style
       layout: { ...layout },
       coverBorderColor,
       coverBorderWidth,
       coverBorderStyle,
       coverOverlay,
       coverOverlayColor,
       // Inside content
       frontText,
       insidePages: [...insidePages],
       insideTextEntries: JSON.parse(JSON.stringify(insideTextEntries)),
       // Inside layout and style
       insideLayout: { ...insideLayout },
       insideStyle: { ...insideStyle },
       insideFont,
       insideFontSize,
       insideFill,
       insideBorderColor,
       insideBorderWidth,
       insideBorderStyle,
       // Signatures
       signatures: JSON.parse(JSON.stringify(signatures)),
     };
   };
   
   // Restore state from a snapshot
   const restoreStateSnapshot = (snapshot) => {
     isRestoringRef.current = true;
     
     // Image/Video
     if (snapshot.imageUrl !== undefined) setImageUrl(snapshot.imageUrl);
     if (snapshot.videoUrl !== undefined) setVideoUrl(snapshot.videoUrl);
     
     // Cover layout and style
     if (snapshot.layout) setLayout(snapshot.layout);
     if (snapshot.coverBorderColor !== undefined) setCoverBorderColor(snapshot.coverBorderColor);
     if (snapshot.coverBorderWidth !== undefined) setCoverBorderWidth(snapshot.coverBorderWidth);
     if (snapshot.coverBorderStyle !== undefined) setCoverBorderStyle(snapshot.coverBorderStyle);
     if (snapshot.coverOverlay !== undefined) setCoverOverlay(snapshot.coverOverlay);
     if (snapshot.coverOverlayColor !== undefined) setCoverOverlayColor(snapshot.coverOverlayColor);
     
     // Inside content
     if (snapshot.frontText !== undefined) setFrontText(snapshot.frontText);
     if (snapshot.insidePages) setInsidePages(snapshot.insidePages);
     if (snapshot.insideTextEntries) setInsideTextEntries(snapshot.insideTextEntries);
     
     // Inside layout and style
     if (snapshot.insideLayout) setInsideLayout(snapshot.insideLayout);
     if (snapshot.insideStyle) setInsideStyle(snapshot.insideStyle);
     if (snapshot.insideFont !== undefined) setInsideFont(snapshot.insideFont);
     if (snapshot.insideFontSize !== undefined) setInsideFontSize(snapshot.insideFontSize);
     if (snapshot.insideFill !== undefined) setInsideFill(snapshot.insideFill);
     if (snapshot.insideBorderColor !== undefined) setInsideBorderColor(snapshot.insideBorderColor);
     if (snapshot.insideBorderWidth !== undefined) setInsideBorderWidth(snapshot.insideBorderWidth);
     if (snapshot.insideBorderStyle !== undefined) setInsideBorderStyle(snapshot.insideBorderStyle);
     
     // Signatures
     if (snapshot.signatures) setSignatures(snapshot.signatures);
     
     // Reset flag after a tick to allow state to settle
     setTimeout(() => {
       isRestoringRef.current = false;
     }, 100);
   };
   
   // Push current state to history (for significant changes)
   // Can pass optional overrides for state that was just updated (since React state is async)
   const pushToHistory = (overrides = {}) => {
     if (isRestoringRef.current) return; // Don't capture during restore
     
     const currentHistory = stateHistoryRef.current;
     const currentIndex = historyIndexRef.current;
     
     const snapshot = { ...captureStateSnapshot(), ...overrides };
     
     // If we're not at the end of history, truncate forward history
     const newHistory = currentHistory.slice(0, currentIndex + 1);
     newHistory.push(snapshot);
     // Keep max 30 items in history
     if (newHistory.length > 30) newHistory.shift();
     
     // Update both refs and state
     stateHistoryRef.current = newHistory;
     historyIndexRef.current = newHistory.length - 1;
     
     setStateHistory(newHistory);
     setHistoryIndex(newHistory.length - 1);
   };
   
   const handleUndo = () => {
     const currentHistory = stateHistoryRef.current;
     const currentIndex = historyIndexRef.current;
     
     if (currentIndex <= 0) return;
     
     const newIndex = currentIndex - 1;
     historyIndexRef.current = newIndex;
     
     setHistoryIndex(newIndex);
     restoreStateSnapshot(currentHistory[newIndex]);
     setSaveStatus('unsaved');
     setHasUnsavedChanges(true);
   };
   
   const handleRedo = () => {
     const currentHistory = stateHistoryRef.current;
     const currentIndex = historyIndexRef.current;
     
     if (currentIndex >= currentHistory.length - 1) return;
     
     const newIndex = currentIndex + 1;
     historyIndexRef.current = newIndex;
     
     setHistoryIndex(newIndex);
     restoreStateSnapshot(currentHistory[newIndex]);
     setSaveStatus('unsaved');
     setHasUnsavedChanges(true);
   };
   
   // Capture initial state on mount
   useEffect(() => {
     if (stateHistory.length === 0 && imageUrl) {
       const initialSnapshot = captureStateSnapshot();
       setStateHistory([initialSnapshot]);
       stateHistoryRef.current = [initialSnapshot];
       setHistoryIndex(0);
       historyIndexRef.current = 0;
     }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [imageUrl]);
   
   // Debounced state capture for text and style changes
   const historyTimeoutRef = useRef(null);
   const lastSnapshotRef = useRef(null);
   
   // Helper to check if state has changed significantly
   const hasStateChanged = () => {
     if (!lastSnapshotRef.current) return true;
     const current = captureStateSnapshot();
     const last = lastSnapshotRef.current;
     
     // Check key fields for changes
     return (
       current.frontText !== last.frontText ||
       JSON.stringify(current.insidePages) !== JSON.stringify(last.insidePages) ||
       JSON.stringify(current.insideTextEntries) !== JSON.stringify(last.insideTextEntries) ||
       JSON.stringify(current.layout) !== JSON.stringify(last.layout) ||
       current.insideFont !== last.insideFont ||
       current.insideFontSize !== last.insideFontSize ||
       current.insideFill !== last.insideFill
     );
   };
   
   // Debounced push to history on significant changes (text, style)
   useEffect(() => {
     if (isRestoringRef.current) return;
     if (stateHistory.length === 0) return; // Wait for initial state
     
     // Clear any pending timeout
     if (historyTimeoutRef.current) {
       clearTimeout(historyTimeoutRef.current);
     }
     
     // Set a new timeout to capture state after 1.5 seconds of inactivity
     historyTimeoutRef.current = setTimeout(() => {
       if (hasStateChanged()) {
         pushToHistory();
         lastSnapshotRef.current = captureStateSnapshot();
       }
     }, 1500);
     
     return () => {
       if (historyTimeoutRef.current) {
         clearTimeout(historyTimeoutRef.current);
       }
     };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [frontText, insidePages, insideTextEntries, layout, insideFont, insideFontSize, insideFill, insideLayout, insideStyle]);
  
    /* ===========================================================
       AI image edit
       ========================================================== */
    const runAiEdit = async () => {
      if (!prompt.trim() || !imageUrl) {
        setAiError("Please enter a prompt");
        return;
      }
      setAiLoading(true);
      setAiError("");
      setAiProgress({ stage: "Starting...", percent: 0 });
      
      // Save current state to history before editing
      pushToHistory();
      
      try {
        console.log("[runAiEdit] Starting AI edit", {
          imageUrl,
          prompt: prompt.trim()
        });
        
        // Enhance prompt for better results with color harmony
        const enhancedPrompt = `${prompt.trim()}. Maintain the overall composition, style, and color palette while making the requested changes throughout the image. Preserve the visual harmony and ensure realistic lighting consistency.`;
        
        // Progress callback for real-time updates
        const handleProgress = (progressData) => {
          setAiProgress(progressData);
        };
        
        const url = await applyDalleEdit(imageUrl, null, enhancedPrompt, "", "1536x1024", handleProgress);
        console.log("[runAiEdit] AI edit successful", { newUrl: url });
        
        // Set new image and push to history with the new URL
        setImageUrl(url);
        pushToHistory({ imageUrl: url });
         
         // Force auto-save after AI operation to ensure new image is saved
         setTimeout(() => {
           console.log("[runAiEdit] Triggering forced auto-save after AI operation");
           // Update save status to trigger auto-save immediately
           setSaveStatus('unsaved');
           setHasUnsavedChanges(true);
         }, 100);
         
         setAiPanelOpen(false);
         setPrompt("");
         setAiProgress({ stage: "", percent: 0 });
             } catch (err) {
        console.error("[runAiEdit] AI edit failed", err);
        // Surface backend-provided error message when available (e.g., rate-limit errors)
        const errorDetail = err?.response?.data?.detail;
        let msg = "AI edit failed. Please try again.";
        
        if (typeof errorDetail === 'string') {
          msg = errorDetail;
        } else if (typeof errorDetail === 'object' && errorDetail?.message) {
          msg = errorDetail.message;
        } else if (err?.response?.data?.message) {
          msg = err.response.data.message;
        }
        
        setAiError(msg);
        setAiProgress({ stage: "", percent: 0 });
      } finally {
         setAiLoading(false);
       }
     };
   
   /* ===========================================================
      Photo Swap functionality
      ========================================================== */
   const handleReferenceUpload = (e) => {
     const file = e.target.files[0];
     if (file) {
       setReferenceImage(file);
       setReferencePreview(URL.createObjectURL(file));
       setAiError("");
     }
   };
   
   const handleReferenceDrop = (e) => {
     e.preventDefault();
     e.stopPropagation();
     setIsDraggingRef(false);
     
     const file = e.dataTransfer.files[0];
     if (file && file.type.startsWith('image/')) {
       setReferenceImage(file);
       setReferencePreview(URL.createObjectURL(file));
       setAiError("");
     } else {
       setAiError("Please drop an image file");
     }
   };
   
   const handleReferenceDragOver = (e) => {
     e.preventDefault();
     e.stopPropagation();
     setIsDraggingRef(true);
   };
   
   const handleReferenceDragLeave = (e) => {
     e.preventDefault();
     e.stopPropagation();
     setIsDraggingRef(false);
   };
   
   const clearReferenceImage = () => {
     setReferenceImage(null);
     setReferencePreview(null);
     if (referenceInputRef.current) referenceInputRef.current.value = '';
   };
   
   const runPhotoSwap = async () => {
     if (!prompt.trim()) {
       setAiError("Please describe what you want to swap");
       return;
     }
     if (!referenceImage) {
       setAiError("Please upload a reference image for swapping");
       return;
     }
     if (!imageUrl) {
       setAiError("No image to edit");
       return;
     }
     
     setAiLoading(true);
     setAiError("");
     setAiProgress({ stage: "Starting photo swap...", percent: 10 });
     
     // Save current state to history before editing
     pushToHistory();
     
     try {
       console.log("[runPhotoSwap] Starting photo swap", {
         imageUrl,
         hasReference: !!referenceImage,
         prompt: prompt.trim()
       });
       
       setAiProgress({ stage: "Downloading template image...", percent: 20 });
       
       // Fetch the current image as a File object
       const response = await fetch(imageUrl);
       const blob = await response.blob();
       const templateFile = new File([blob], 'template.png', { type: 'image/png' });
       
       setAiProgress({ stage: "Processing with AI...", percent: 40 });
       
       const data = await photoSwap({
         prompt: prompt.trim(),
         templateImage: templateFile,
         referenceImage: referenceImage,
         useProModel: false,
       });
       
       setAiProgress({ stage: "Finalizing result...", percent: 80 });
       
       // Handle the result URL
       let resultUrl = data.public_url;
       if (resultUrl.startsWith('/uploads/')) {
         resultUrl = `${API_URL}${resultUrl}`;
       }
       
       console.log("[runPhotoSwap] Photo swap successful", { newUrl: resultUrl });
       
       // Set new image and push to history with the new URL
       setImageUrl(resultUrl);
       pushToHistory({ imageUrl: resultUrl });
       
       setAiProgress({ stage: "Complete!", percent: 100 });
       
       // Force auto-save after AI operation
       setTimeout(() => {
         console.log("[runPhotoSwap] Triggering forced auto-save after photo swap");
         setSaveStatus('unsaved');
         setHasUnsavedChanges(true);
       }, 100);
       
       // Reset state
       setAiPanelOpen(false);
       setPrompt("");
       setPhotoSwapMode(false);
       clearReferenceImage();
       setAiProgress({ stage: "", percent: 0 });
       
     } catch (err) {
       console.error("[runPhotoSwap] Photo swap failed", err);
       const errorDetail = err?.response?.data?.detail;
       let msg = "Photo swap failed. Please try again.";
       
       if (typeof errorDetail === 'string') {
         msg = errorDetail;
       } else if (typeof errorDetail === 'object' && errorDetail?.message) {
         msg = errorDetail.message;
       }
       
       setAiError(msg);
       setAiProgress({ stage: "", percent: 0 });
     } finally {
       setAiLoading(false);
     }
   };
   
     /* ===========================================================
        Invite collaborator
        ========================================================== */
  const handleInvite = async () => {
    // Clear previous errors and success messages
    setInviteError("");
    setInviteSuccess("");
    
    // Check if user has can_collaborate permission (Preview tier users don't)
    if (subscriptionLimits && !subscriptionLimits.can_collaborate) {
      setInviteError({
        message: "Make it a group card! 🤝",
        action: "Upgrade to invite friends and family to sign together.",
        showUpgrade: true,
        isPreviewLimit: true
      });
      return;
    }
    
    // Validate form before proceeding
    const validationErrors = validateInviteForm();
    if (showValidationErrors(validationErrors, setInviteError)) {
      return;
    }
    
    setInviteLoading(true);
    try {
      await inviteContributor(cardId, inviteEmail, senderName, recipientName, category);
      setInviteSuccess("Invite sent successfully! 🎉");
      setInviteEmail("");
      const shareLink = `${window.location.origin}/collaborate/${cardId}`;
      navigator.clipboard.writeText(shareLink);
      
      // Track card shared event for Facebook Pixel
      fbPixel.trackCardShared('collaboration_invite');
      
    } catch (err) {
      console.error(err);
      
      // Handle subscription-related errors
      const errorDetail = err?.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object') {
        if (errorDetail.reason === 'subscription_required') {
          setInviteError({
            message: errorDetail.message,
            showUpgrade: true,
            suggestedPlans: errorDetail.suggested_plans
          });
        } else if (errorDetail.reason === 'limit_exceeded') {
          setInviteError({
            message: errorDetail.message,
            showUpgrade: true,
            suggestedPlans: errorDetail.suggested_plans,
            currentUsage: errorDetail.current_usage,
            limit: errorDetail.limit
          });
        } else {
          setInviteError({
            message: errorDetail.message || "Failed to send invite. Please try again.",
            showUpgrade: false
          });
        }
      } else {
        // Handle simple string error messages
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                            err?.response?.data?.message || 
                            "Failed to send invite. Please try again.";
        setInviteError({
          message: errorMessage,
          showUpgrade: false
        });
      }
    } finally {
      setInviteLoading(false);
    }
  };
   
     /* ===========================================================
        Schedule send
        ========================================================== */
  const handleSchedule = async () => {
    // Clear previous errors and success messages
    setScheduleError("");
    setScheduleSuccess("");
    
    // Check if user has can_send permission (Preview tier users don't)
    if (subscriptionLimits && !subscriptionLimits.can_send) {
      setScheduleError({
        message: "Your card is ready! 🎉",
        action: "Upgrade to send it and make their day.",
        showUpgrade: true,
        isPreviewLimit: true
      });
      return;
    }
    
    // Validate form before proceeding
    const validationErrors = validateScheduleForm();
    if (showValidationErrors(validationErrors, setScheduleError)) {
      return;
    }
    
    setScheduleLoading(true);
    try {
      // First, ensure the card is saved with latest changes
      console.log("[Schedule] Saving card before scheduling...");
      // Only include card_id if we have a real Firestore ID (not AI-generated temp ID)
      const isRealId = cardId && !cardId.startsWith("ai_");
      const payload = {
        ...(isRealId ? { card_id: cardId } : {}),
        title: String(frontText || "").slice(0, 30) || "Untitled Card",
        front_text: String(frontText || "").trim(),
        inside_text: insidePages.map((p) => String(p || "").trim()).filter(Boolean).join("\n"),
        image_url: imageUrl || "",
        video_url: videoUrl || "",           // Video greeting card URL
        media_type: mediaType || "image",    // "image" or "video" to distinguish card type
        layout: {
          ...layout,
          // Keep x/y as undefined if not set - this allows auto-centering on reload
          x: typeof layout.x === 'number' ? Math.round(layout.x) : undefined,
          y: typeof layout.y === 'number' ? Math.round(layout.y) : undefined,
          width: Math.round(layout.width || cardWidth * 0.8),
          height: Math.round(layout.height || cardHeight * 0.8),
          rotation: typeof layout.rotation === 'number' ? layout.rotation : 0,
          scaleX: typeof layout.scaleX === 'number' ? layout.scaleX : 1,
          scaleY: typeof layout.scaleY === 'number' ? layout.scaleY : 1,
          align: layout.align || "center",
          fontFamily: layout.fontFamily || "Lilita One",
          fontSize: layout.fontSize || 48,
          fill: layout.fill || "#000000",
          stroke: layout.stroke || "",
          strokeWidth: layout.strokeWidth || 0,
          animStyle: layout.animStyle || "none",
          text: String(frontText || "").trim(),
          borderColor: coverBorderColor || "",
          borderWidth: coverBorderWidth || 0,
          borderStyle: coverBorderStyle || "solid",
          overlay: coverOverlay || "none",
          overlayColor: coverOverlayColor || "#1F2937"
        },
        inside_layout: {
          ...insideLayout,
          x: typeof insideLayout.x === 'number' ? Math.round(insideLayout.x) : cardWidth * 0.1,
          y: typeof insideLayout.y === 'number' ? Math.round(insideLayout.y) : cardHeight * 0.1,
          width: Math.round(insideLayout.width || cardWidth * 0.8),
          height: Math.round(insideLayout.height || cardHeight * 0.8),
          rotation: typeof insideLayout.rotation === 'number' ? insideLayout.rotation : 0,
          scaleX: typeof insideLayout.scaleX === 'number' ? insideLayout.scaleX : 1,
          scaleY: typeof insideLayout.scaleY === 'number' ? insideLayout.scaleY : 1,
          align: insideLayout.align || "left",
          fontFamily: insideFont || "'Inter', sans-serif",
          fontSize: insideFontSize || 24,
          fill: insideFill || "#000000",
          stroke: insideLayout.stroke || "",
          strokeWidth: insideLayout.strokeWidth || 0,
          animStyle: insideLayout.animStyle || "none",
          text: String(insidePages[currentInsidePage] || "").trim()
        },
        inside_style: {
          fontFamily: insideFont || "'Inter', sans-serif",
          fontSize: insideFontSize || 24,
          fill: insideFill || "#000000",
          borderColor: insideBorderColor || "",
          borderWidth: insideBorderWidth || 0,
          borderStyle: insideBorderStyle || "solid"
        },
        signatures: signatures.map((s) => String(s || "").trim()).filter(Boolean),
        // Collaborator text entries
        inside_text_entries: insideTextEntries,
        recipient: sloganRecipient || "",
        occasion: sloganOccasion || "",
        category: category || "",
        card_size: `${imageDimensions.width}x${imageDimensions.height}`,
        // Don't set status when saving before scheduling
        status: undefined
      };

      // Save the card first
      const savedCardId = await saveCardDraft(payload);
      if (!isRealId) {
        setCardId(savedCardId);
      }
      
      // Use the saved ID when we just created a new card, otherwise use existing cardId
      const effectiveCardId = isRealId ? cardId : savedCardId;

      console.log("[Schedule] Card saved successfully, proceeding with scheduling...");

      // Now proceed with scheduling
      await scheduleCardSend(effectiveCardId, sendDate, recipientEmail, recipientName, senderName);
      const scheduleDate = new Date(sendDate);
      setScheduleSuccess("Card scheduled successfully! 📅");
      
      // Track card sent/scheduled event for Facebook Pixel
      fbPixel.trackCustomEvent('CardSent', {
        content_type: 'greeting_card',
        card_id: effectiveCardId,
        category: category,
        scheduled: true,
      });
      
      // Show success message with proper success modal
      const message = `✅ Card scheduled successfully!\n\nYour card will be sent to:\n📧 ${recipientEmail}\n📅 ${scheduleDate.toLocaleDateString()} at ${scheduleDate.toLocaleTimeString()}\n\nThe recipient will receive it directly in their inbox.`;
      showSuccess(message);
      
    } catch (err) {
      console.error("[Schedule] Error:", err);
      
      // Handle backend errors with better messaging
      const errorDetail = err?.response?.data?.detail;
      let errorMessage = "Failed to schedule card. Please try again.";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (errorDetail?.message) {
        errorMessage = errorDetail.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setScheduleError({
        message: errorMessage,
        showUpgrade: false
      });
      
    } finally {
      setScheduleLoading(false);
    }
  };
   
     /* ===========================================================
        Render
        ========================================================== */
   /* preview state - use actual image dimensions for proper cover aspect ratio */
   const previewCardState = {
     imageUrl,
     videoUrl,                    // NEW: Video URL for video greeting cards
     mediaType,                   // NEW: "image" or "video"
     cardWidth: imageDimensions.width,
     cardHeight: imageDimensions.height,
      text: {
        ...layout,
        text: frontText,
      },
      setText: handleCoverTextChange,
      inside: insidePages[currentInsidePage],
      setInside: updateInsidePage,
      insideFont,
      insideFontSize,
      insideFill,
      insideBorderColor,
      insideBorderWidth,
      insideBorderStyle,
      coverBorderColor,
      coverBorderWidth,
      coverBorderStyle,
      coverOverlay,
      coverOverlayColor,
      signatures,
      insidePages,
      insideLayout, // Include inside text positioning for preview
      insideTextEntries, // Collaborator text entries with signatures
    };

     // Define sync handlers to keep text state in sync with layout updates (hoisted functions)
         function handleCoverTextChange(props) {
      // Handle both object props and string text updates
      if (typeof props === "string") {
        // Direct text update - let EditableText handle auto-centering
        setFrontText(props);
        setLayout(prev => ({
          ...prev,
          text: props,
          align: "center", // Ensure cover text stays centered
        }));
      } else if (props && typeof props === "object") {
        // Strip out Konva-only props that should not persist in our layout state
        const { draggable: _omitDraggable, ...cleanProps } = props;

        // Layout property updates
        setLayout(prev => ({
          ...prev,
          ...cleanProps,
          // Ensure exact position values are saved
          x: typeof cleanProps.x === 'number' ? cleanProps.x : prev.x,
          y: typeof cleanProps.y === 'number' ? cleanProps.y : prev.y,
          text: typeof cleanProps.text === "string" ? cleanProps.text : prev.text,
          align: "center", // Always keep cover text centered
          // Always re-enable dragging inside the editor
          draggable: true,
        }));
        if (typeof cleanProps.text === "string") {
          setFrontText(cleanProps.text);
        }
      }
    }

         function handleInsideTextChange(props) {
      // Handle both object props and string text updates
      if (typeof props === "string") {
        // Direct text update from canvas (e.g., double-click edit)
        if (props !== insidePages[currentInsidePage]) {
          updateInsidePage(props);
        }
      } else if (props && typeof props === "object") {
        // Handle layout changes (dragging, positioning) - only update if actually changed
        const hasLayoutChange = (
          (props.x !== undefined && props.x !== insideLayout.x) ||
          (props.y !== undefined && props.y !== insideLayout.y) ||
          (props.width !== undefined && props.width !== insideLayout.width) ||
          (props.height !== undefined && props.height !== insideLayout.height) ||
          (props.rotation !== undefined && props.rotation !== insideLayout.rotation) ||
          (props.scaleX !== undefined && props.scaleX !== insideLayout.scaleX) ||
          (props.scaleY !== undefined && props.scaleY !== insideLayout.scaleY)
        );

        if (hasLayoutChange) {
          setInsideLayout(prev => ({
            ...prev,
            x: props.x !== undefined ? props.x : prev.x,
            y: props.y !== undefined ? props.y : prev.y,
            width: props.width !== undefined ? props.width : prev.width,
            height: props.height !== undefined ? props.height : prev.height,
            rotation: props.rotation !== undefined ? props.rotation : prev.rotation,
            scaleX: props.scaleX !== undefined ? props.scaleX : prev.scaleX,
            scaleY: props.scaleY !== undefined ? props.scaleY : prev.scaleY,
            align: "left", // Keep inside text left-aligned and freely draggable
          }));
        }

        // Handle text content changes from canvas - only if different
        if (props.text !== undefined && props.text !== insidePages[currentInsidePage]) {
          updateInsidePage(props.text);
        }
      }
    }

     /* Ensure draggable stays enabled after returning from preview */
     useEffect(() => {
       if (layout && layout.draggable === false) {
         setLayout((prev) => {
           const { draggable: _omit, ...rest } = prev;
           return rest;
         });
       }
     }, [layout]);

     /* -----------------------------------------------------------
        Extend cover to full canvas (AI out-painting)
        ----------------------------------------------------------- */
     const [extendLoading, setExtendLoading] = useState(false);

     const extendToCanvas = async () => {
       if (!imageUrl || extendLoading) return;
       
       // Basic validation checks
       if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
         showError("Invalid image URL. Please try uploading a new image.");
         return;
       }
       
       // Ensure we're using the correct API endpoint for templates instead of static files
       let processedImageUrl = imageUrl;
       if (imageUrl.includes('/static/templates/')) {
         processedImageUrl = imageUrl.replace('/static/templates/', '/templates/');
       }
       
       console.log("[extendToCanvas] Using image URL:", processedImageUrl);
       
       setExtendLoading(true);
       try {
         // Load the original image
         const img = await loadImage(processedImageUrl);
         console.log("[extendToCanvas] Original image loaded:", { width: img.width, height: img.height });
         
         // Calculate what kind of extension is needed
         const originalWidth = img.width;
         const originalHeight = img.height;
         
         // Target dimensions for the final card
         const targetWidth = cardWidth;   // e.g., 1024 for 4:3 cards
         const targetHeight = cardHeight; // e.g., 768 for 4:3 cards
         
         console.log("[extendToCanvas] Dimensions:", {
           original: `${originalWidth}x${originalHeight}`,
           target: `${targetWidth}x${targetHeight}`
         });
         
         // Determine if we need to extend and in which directions
         const widthDiff = Math.max(0, targetWidth - originalWidth);
         const heightDiff = Math.max(0, targetHeight - originalHeight);
         const needsExtension = widthDiff > 0 || heightDiff > 0;
         
         if (!needsExtension) {
           showError("Image is already at target size or larger. No extension needed.");
           return;
         }
         
         // Log what kind of extension is needed
         console.log("[extendToCanvas] Extension needed:", {
           widthIncrease: widthDiff,
           heightIncrease: heightDiff,
           percentageIncrease: {
             width: ((widthDiff / originalWidth) * 100).toFixed(1) + '%',
             height: ((heightDiff / originalHeight) * 100).toFixed(1) + '%'
           }
         });
         
         // DALL-E 2 requires square images. Find the best square size that accommodates our needs
         const maxDimension = Math.max(targetWidth, targetHeight, originalWidth, originalHeight);
         let squareSize = 1024; // Default to largest DALL-E 2 size
         
         // Choose appropriate square size for DALL-E 2
         if (maxDimension <= 256) squareSize = 256;
         else if (maxDimension <= 512) squareSize = 512;
         else squareSize = 1024;
         
         console.log("[extendToCanvas] Using square size for DALL-E 2:", squareSize);
         
         // Center the original image in the square canvas
         const offsetX = Math.floor((squareSize - originalWidth) / 2);
         const offsetY = Math.floor((squareSize - originalHeight) / 2);
         
         console.log("[extendToCanvas] Extension plan:", {
           originalSize: `${originalWidth}x${originalHeight}`,
           targetSize: `${targetWidth}x${targetHeight}`,
           squareSize: `${squareSize}x${squareSize}`,
           offset: { x: offsetX, y: offsetY },
           extensionAreas: {
             left: offsetX,
             right: squareSize - (offsetX + originalWidth),
             top: offsetY,  
             bottom: squareSize - (offsetY + originalHeight)
           }
         });
         
         // Create the square canvas with the original image preserved
         const canvas = document.createElement('canvas');
         const ctx = canvas.getContext('2d');
         canvas.width = squareSize;
         canvas.height = squareSize;
         
         // Fill with a neutral background color that matches common template backgrounds
         ctx.fillStyle = '#ffffff'; // White background for better DALL-E results
         ctx.fillRect(0, 0, squareSize, squareSize);
         
         // Place the original image exactly in the center, preserving its original size and quality
         ctx.drawImage(img, offsetX, offsetY, originalWidth, originalHeight);
         
         console.log("[extendToCanvas] Original image preserved at offset:", { offsetX, offsetY });

         // Create a precise mask for DALL-E 2 outpainting
         const maskCanvas = document.createElement('canvas');
         const maskCtx = maskCanvas.getContext('2d');
         maskCanvas.width = squareSize;
         maskCanvas.height = squareSize;
         
         // Create the mask: white = extend/edit areas, black = preserve original
         maskCtx.fillStyle = 'white'; // Start with all white (extend everything)
         maskCtx.fillRect(0, 0, squareSize, squareSize);
         
         // Paint black rectangle over the original image area to preserve it completely
         maskCtx.fillStyle = 'black';
         maskCtx.fillRect(offsetX, offsetY, originalWidth, originalHeight);
         
         // Calculate extension statistics
         const totalPixels = squareSize * squareSize;
         const preservedPixels = originalWidth * originalHeight;
         const extensionPixels = totalPixels - preservedPixels;
         const extensionPercentage = ((extensionPixels / totalPixels) * 100).toFixed(1);
         
         console.log(`[extendToCanvas] Outpainting mask created:`, {
           canvasSize: `${squareSize}x${squareSize}`,
           originalImageSize: `${originalWidth}x${originalHeight}`,
           extensionPixels,
           totalPixels,
           extensionPercentage: extensionPercentage + '%',
           maskType: 'square outpainting (DALL-E 2 compatible)'
         });

         // Check if we have meaningful areas to extend
         if (extensionPixels === 0) {
           throw new Error('No extension areas found. Image is already at maximum size.');
         }
         
         // Very low threshold - even small extensions can be valuable
         if (extensionPixels < totalPixels * 0.01) { // Only 1% threshold
           console.warn(`[extendToCanvas] Small extension area (${extensionPercentage}%), but proceeding anyway`);
         }

         // Convert canvases to blobs
         const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
         const maskBlob = await new Promise(resolve => maskCanvas.toBlob(resolve, 'image/png'));

         // Create form data for DALL-E 2 outpainting with better prompt
         const formData = new FormData();
         formData.append('image', new File([imageBlob], 'template.png', { type: 'image/png' }));
         formData.append('mask', new File([maskBlob], 'mask.png', { type: 'image/png' }));
         
         // Enhanced prompt for better outpainting results
         const outpaintPrompt = 
           'Seamlessly extend the background and environment around the edges while keeping the center image exactly the same. ' +
           'Continue the artistic style, colors, lighting, and atmosphere naturally. ' +
           'Add more of the same background elements, patterns, or scenery that would logically exist beyond the current frame. ' +
           'Ensure the extension blends perfectly with the existing image edges.';
         
         formData.append('prompt', outpaintPrompt);
         formData.append('size', `${squareSize}x${squareSize}`); // Use square size for DALL-E 2
         formData.append('skip_crop', 'true'); // Don't crop the result
         formData.append('target_w', String(targetWidth));
         formData.append('target_h', String(targetHeight));

         console.log("[extendToCanvas] Sending optimized outpainting request...");

         // Make the API call
         const { data } = await dalleEdit(formData);
         
         if (!data || !data.public_url) {
           throw new Error('Invalid response from server - missing public_url');
         }

         console.log("[extendToCanvas] Outpainting successful! Extended image:", data.public_url);
         setImageUrl(data.public_url);
         
         // Show success message
         setTimeout(() => {
           console.log("[extendToCanvas] Background extended successfully while preserving original template!");
         }, 500);
         
       } catch (error) {
         console.error('[extendToCanvas] Outpainting error:', error);
         showError(error.message || 'Failed to extend background. Please try again.');
       } finally {
         setExtendLoading(false);
       }
     };

     /* Test function for AI edit */


     /* ===========================================================
        Validation and Error Handling Utilities
        ========================================================== */
    
    // Auto-scroll to a section with error highlighting
    const scrollToSection = (sectionId, fieldId = null) => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Highlight the section briefly
        section.style.boxShadow = '0 0 20px rgba(239, 68, 68, 0.5)';
        section.style.transition = 'box-shadow 0.3s ease';
        setTimeout(() => {
          section.style.boxShadow = '';
        }, 2000);
        
        // Focus on specific field if provided
        if (fieldId) {
          setTimeout(() => {
            const field = document.getElementById(fieldId);
            if (field) {
              field.focus();
              field.style.borderColor = '#ef4444';
              field.style.borderWidth = '2px';
              setTimeout(() => {
                field.style.borderColor = '';
                field.style.borderWidth = '';
              }, 3000);
            }
          }, 500);
        }
      }
    };

    // Comprehensive validation for collaboration invite
    const validateInviteForm = () => {
      const errors = [];
      
      if (!cardId) {
        errors.push({
          message: "Please save your card first before inviting collaborators",
          action: "Save your card using the 'Save Draft' button",
          scrollTo: "save-section",
          fieldId: null
        });
      }
      
      if (!senderName.trim()) {
        errors.push({
          message: "Your name is required to send invitations",
          action: "Enter your name in the collaboration section",
          scrollTo: "collaboration-section",
          fieldId: "sender-name-input"
        });
      }
      
      if (!recipientName.trim()) {
        errors.push({
          message: "Recipient name is required for collaboration",
          action: "Enter who this card is for in the collaboration section",
          scrollTo: "collaboration-section", 
          fieldId: "recipient-name-input"
        });
      }
      
      if (!inviteEmail.trim()) {
        errors.push({
          message: "Collaborator email address is required",
          action: "Enter the email address of the person you want to invite",
          scrollTo: "collaboration-section",
          fieldId: "invite-email-input"
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
        errors.push({
          message: "Please enter a valid email address",
          action: "Check the email format (example@domain.com)",
          scrollTo: "collaboration-section",
          fieldId: "invite-email-input"
        });
      }
      
      return errors;
    };

    // Comprehensive validation for scheduling
    const validateScheduleForm = () => {
      const errors = [];
      
      if (!cardId) {
        errors.push({
          message: "Please save your card first before scheduling",
          action: "Save your card using the 'Save Draft' button",
          scrollTo: "save-section",
          fieldId: null
        });
      }
      
      if (!senderName.trim()) {
        errors.push({
          message: "Your name is required for card delivery",
          action: "Enter your name in the collaboration section above",
          scrollTo: "collaboration-section",
          fieldId: "sender-name-input"
        });
      }
      
      if (!recipientEmail.trim()) {
        errors.push({
          message: "Recipient email is required for delivery",
          action: "Enter the email address where the card should be sent",
          scrollTo: "schedule-section",
          fieldId: "recipient-email-input"
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
        errors.push({
          message: "Please enter a valid recipient email address",
          action: "Check the email format (example@domain.com)",
          scrollTo: "schedule-section",
          fieldId: "recipient-email-input"
        });
      }
      
      if (!recipientName.trim()) {
        errors.push({
          message: "Recipient name is required for personalization",
          action: "Enter who this card is for (syncs with collaboration section)",
          scrollTo: "schedule-section",
          fieldId: "recipient-name-schedule-input"
        });
      }
      
      if (!sendDate) {
        errors.push({
          message: "Please select when to send the card",
          action: "Choose a date and time for delivery",
          scrollTo: "schedule-section",
          fieldId: "send-date-input"
        });
      } else {
        const scheduleDate = new Date(sendDate);
        const now = new Date();
        if (scheduleDate <= now) {
          errors.push({
            message: "Send date must be in the future",
            action: "Select a date and time that hasn't passed yet",
            scrollTo: "schedule-section",
            fieldId: "send-date-input"
          });
        }
      }
      
      return errors;
    };

    // Display validation errors with auto-scroll
    const showValidationErrors = (errors, setErrorState) => {
      if (errors.length === 0) return false;
      
      const firstError = errors[0];
      setErrorState({
        message: firstError.message,
        action: firstError.action,
        showUpgrade: false,
        allErrors: errors
      });
      
      // Auto-scroll to the first error
      if (firstError.scrollTo) {
        setTimeout(() => scrollToSection(firstError.scrollTo, firstError.fieldId), 100);
      }
      
      return true;
    };

     return (
       <div className="min-h-screen bg-gray-50 pb-32">
         {/* Save Status Indicator */}
         {(hasUnsavedChanges || saveStatus !== 'saved') && (
           <div className="fixed top-4 right-4 z-50">
             <div className={`px-3 py-2 rounded-lg shadow-lg text-sm font-medium transition-all ${
               saveStatus === 'saving' 
                 ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                 : saveStatus === 'saved' && !hasUnsavedChanges
                 ? 'bg-green-100 text-green-800 border border-green-300' 
                 : saveStatus === 'error'
                 ? 'bg-red-100 text-red-800 border border-red-300'
                 : hasUnsavedChanges
                 ? 'bg-blue-100 text-blue-800 border border-blue-300'
                 : 'bg-gray-100 text-gray-800 border border-gray-300'
             }`}>
               {saveStatus === 'saving' ? (
                 <div className="flex items-center gap-2">
                   <div className="animate-spin rounded-full h-3 w-3 border-2 border-yellow-600 border-t-transparent"></div>
                   <span>Saving...</span>
                 </div>
               ) : saveStatus === 'saved' && !hasUnsavedChanges ? (
                 <div className="flex items-center gap-2">
                   <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                   </svg>
                   <span>All changes saved</span>
                   {lastSavedAt && (
                     <span className="text-xs opacity-75">
                       at {lastSavedAt.toLocaleTimeString()}
                     </span>
                   )}
                 </div>
               ) : saveStatus === 'error' ? (
                 <div className="flex items-center gap-2">
                   <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                   </svg>
                   <span>Save failed</span>
                 </div>
               ) : hasUnsavedChanges ? (
                 <div className="flex items-center gap-2">
                   <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                   <span>Unsaved changes</span>
                 </div>
               ) : null}
             </div>
           </div>
         )}
         
         <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
             {/* left column – preview and AI edit */}
             <div className="lg:col-span-7 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto bg-gray-100 rounded-lg p-4 sm:p-6 lg:p-8">
             <div className="flex justify-center items-center w-full">
              <div
                className="relative rounded-lg overflow-hidden inline-flex"
                style={{
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25), 0 4px 10px rgba(0, 0, 0, 0.15)",
                  // Only show background for inside tab (white background)
                  backgroundColor: tab === "inside" ? "#ffffff" : "transparent",
                  // Let canvas determine size, but constrain max dimensions
                  maxWidth: "100%",
                  maxHeight: "70vh",
                }}
               >
                 
              {/* Render VideoCanvas for video templates on cover, GreetingCardCanvas for images/inside */}
              {mediaType === "video" && tab === "cover" ? (
                (() => {
                  // Calculate scaled dimensions to fit within container while preserving aspect ratio
                  const maxWidth = Math.min(window.innerWidth * 0.9, 800); // reasonable max width
                  const maxHeight = window.innerHeight * 0.7; // 70vh equivalent
                  const aspectRatio = imageDimensions.width / imageDimensions.height;
                  
                  let scaledWidth = imageDimensions.width;
                  let scaledHeight = imageDimensions.height;
                  
                  // Scale down if exceeds max dimensions
                  if (scaledWidth > maxWidth) {
                    scaledWidth = maxWidth;
                    scaledHeight = scaledWidth / aspectRatio;
                  }
                  if (scaledHeight > maxHeight) {
                    scaledHeight = maxHeight;
                    scaledWidth = scaledHeight * aspectRatio;
                  }
                  
                  return (
                <div className="relative" style={{ width: scaledWidth, height: scaledHeight }}>
                  {/* Video background - no controls, just loops */}
                  {/* Safari-optimized: uses preload="auto", crossOrigin, and webkit-playsinline */}
                  <video
                    src={videoUrl ? `${videoUrl}#t=0.001` : videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    webkit-playsinline="true"
                    preload="auto"
                    crossOrigin="anonymous"
                    className="w-full h-full"
                    style={{ objectFit: 'contain' }}
                    onLoadedMetadata={(e) => {
                      console.log('[EditingPage] Video loaded:', e.target.videoWidth, e.target.videoHeight);
                      if (e.target.videoWidth && e.target.videoHeight && !dimensionsRestoredRef.current) {
                        setImageDimensions({ width: e.target.videoWidth, height: e.target.videoHeight });
                      }
                    }}
                    onError={(e) => {
                      console.error('[EditingPage] Video load error:', e);
                    }}
                  />
                   
                  {/* Frame/Border overlay for video - positioned to wrap exactly */}
                  {coverBorderWidth > 0 && coverBorderColor && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        border: `${coverBorderWidth}px ${coverBorderStyle} ${coverBorderColor}`,
                        boxSizing: 'border-box',
                        zIndex: 10,
                      }}
                    />
                  )}
                   
                  {/* Konva Stage overlay for video text - enables dragging */}
                  {frontText && (() => {
                    const scaleFactor = scaledWidth / imageDimensions.width;
                    const padding = 40;
                    const bounds = {
                      x: padding,
                      y: 0,
                      width: imageDimensions.width - padding * 2,
                      height: imageDimensions.height,
                    };
                    const imageBounds = {
                      x: 0,
                      y: 0,
                      width: imageDimensions.width,
                      height: imageDimensions.height,
                    };
                    const textProps = {
                      ...layout,
                      text: frontText,
                      x: layout.x ?? bounds.x,
                      y: layout.y ?? (imageDimensions.height * 0.3),
                      width: layout.width || bounds.width,
                      fontSize: layout.fontSize || 72,
                      fontFamily: layout.fontFamily || 'Lilita One',
                      fontWeight: layout.fontWeight || 700,
                      fill: layout.fill || '#000000',
                      align: layout.align || 'center',
                      lineHeight: layout.lineHeight || 1.2,
                      letterSpacing: layout.letterSpacing || 1,
                      stroke: layout.stroke || '',
                      strokeWidth: layout.strokeWidth || 0,
                      rotation: layout.rotation || 0,
                      wrap: 'word',
                      draggable: true,
                    };
                    return (
                    <div 
                      className="absolute inset-0"
                      style={{ zIndex: 5 }}
                    >
                      <Stage
                        width={scaledWidth}
                        height={scaledHeight}
                        scaleX={scaleFactor}
                        scaleY={scaleFactor}
                      >
                        <Layer>
                          <EditableText
                            textProps={textProps}
                            onChange={handleCoverTextChange}
                            bounds={bounds}
                            imageBounds={imageBounds}
                          />
                        </Layer>
                      </Stage>
                    </div>
                    );
                  })()}
                   
                   {/* Decorative overlay for video - disabled for video cards */}
                   {/* Note: Overlay options are hidden for video cards, but render existing overlays for backwards compatibility */}
                   {coverOverlay && coverOverlay !== 'none' && (
                     <div
                       className="absolute inset-0 pointer-events-none"
                       style={{
                         backgroundImage: coverOverlay === 'snow-splatter' 
                           ? 'radial-gradient(circle, white 2px, transparent 2px)'
                           : coverOverlay === 'stars'
                           ? 'radial-gradient(circle, gold 1px, transparent 1px)'
                           : 'none',
                         backgroundSize: coverOverlay === 'snow-splatter' ? '30px 30px' : '40px 40px',
                         opacity: 0.6,
                      }}
                    />
                  )}
                </div>
                  );
                })()
              ) : (
                <GreetingCardCanvas
                   side={tab === "cover" ? "front" : "inside"}
                   bgSrc={imageUrl}
                   textProps={
                     tab === "cover"
                       ? { ...layout, text: frontText, align: "center" } // Ensure cover text is centered
                       : { 
                           ...insideLayout, 
                           text: insidePages[currentInsidePage],
                           fontSize: insideFontSize,
                           fontFamily: insideFont,
                           fill: insideFill,
                           draggable: true,
                           align: "left", // Inside text should not auto-center
                           wrap: "word",
                         }
                   }
                   setText={
                     tab === "cover" ? handleCoverTextChange : handleInsideTextChange
                   }
                  cardWidth={imageDimensions.width}
                  cardHeight={imageDimensions.height}
                   insideBorderColor={insideBorderColor}
                   insideBorderWidth={insideBorderWidth}
                   insideBorderStyle={insideBorderStyle}
                   coverBorderColor={coverBorderColor}
                   coverBorderWidth={coverBorderWidth}
                   coverBorderStyle={coverBorderStyle}
                   coverOverlay={coverOverlay}
                   coverOverlayColor={coverOverlayColor}
                   fitToImage={tab === "cover"}
                   // Collaborator text entries - show on inside view
                   insideTextEntries={tab === "inside" ? insideTextEntries : null}
                   onTextEntryChange={(entryId, updatedProps) => {
                     setInsideTextEntries(prev => 
                       prev.map(entry => 
                         entry.id === entryId ? { ...entry, ...updatedProps } : entry
                       )
                     );
                   }}
                 />
               )}

                
                {/* AI Loading Overlay on Card */}
                {aiLoading && (
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg z-50">
                    {/* Centered Lottie Animation */}
                    <div className="w-40 h-40 mb-4">
                      <Lottie 
                        animationData={loadingAnimation} 
                        loop={true}
                        autoplay={true}
                      />
                    </div>
                    
                    {/* Status Message */}
                    <p className="text-center text-xl font-semibold text-purple-700 mb-4">
                      {aiProgress.stage || (photoSwapMode ? "Starting photo swap..." : "Starting AI magic...")}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="w-64 mx-auto">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span>{Math.round(aiProgress.percent || 0)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className={`h-4 rounded-full transition-all duration-500 ease-out ${
                            photoSwapMode 
                              ? 'bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500' 
                              : 'bg-gradient-to-r from-purple-600 via-blue-600 to-teal-500'
                          }`}
                          style={{ width: `${aiProgress.percent || 5}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Helpful Message */}
                    <p className="text-sm text-gray-500 mt-6 text-center max-w-sm px-4">
                      ✨ {photoSwapMode ? "Photo swap" : "AI image generation"} typically takes 1-2 minutes. 
                      Please keep this page open while we create your masterpiece!
                    </p>
                  </div>
                )}
                </div>
               </div>

               {/* Signatures display below card for inside view */}
               {tab === "inside" && signatures.length > 0 && (
                 <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                   <div className="text-sm font-medium text-gray-600 mb-3">Signatures (will appear at bottom of card):</div>
                   <div className="space-y-2">
                     {signatures.map((sig, i) => (
                       <div
                         key={i}
                         className="text-right text-gray-800"
                         style={{
                           fontFamily: insideFont || "'Inter', sans-serif",
                           fontSize: Math.max(14, insideFontSize * 0.7),
                           fontStyle: 'italic',
                         }}
                       >
                         — {sig}
                       </div>
                     ))}
                   </div>
                   <div className="text-xs text-gray-500 mt-2 italic">
                     Preview the card to see how signatures will appear on the final card
                   </div>
                 </div>
               )}

               {/* Collaborator Messages - show entries from collaborators */}
               {tab === "inside" && (hasCollaborators || insideTextEntries.length > 0) && (
                 <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                   <div className="flex items-center justify-between mb-3">
                     <div className="flex items-center gap-2">
                       <UserPlus size={16} className="text-purple-600" />
                       <span className="text-sm font-medium text-purple-700">
                         Collaborator Messages ({insideTextEntries.length})
                       </span>
                     </div>
                     <button
                       onClick={async () => {
                         try {
                           const { data } = await getCardDetails(cardId);
                           if (data.inside_text_entries && Array.isArray(data.inside_text_entries)) {
                             setInsideTextEntries(data.inside_text_entries);
                             console.log("[Manual Refresh] Updated text entries");
                           }
                         } catch (err) {
                           console.error("[Manual Refresh] Failed:", err);
                         }
                       }}
                       className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                       title="Check for new messages from collaborators"
                     >
                       <RotateCcw size={12} />
                       Refresh
                     </button>
                   </div>
                   <div className="space-y-3">
                     {insideTextEntries.map((entry) => (
                       <div
                         key={entry.id}
                         className="flex items-start justify-between bg-white p-3 rounded-lg border border-purple-100"
                       >
                         <div className="flex-1 mr-2">
                           <p 
                             className="text-gray-800 text-sm"
                             style={{
                               fontFamily: entry.fontFamily || insideFont || "'Inter', sans-serif",
                               color: entry.fill || insideFill || "#000000",
                             }}
                           >
                             {entry.text.length > 150 ? entry.text.slice(0, 150) + "..." : entry.text}
                           </p>
                           {entry.signature && (
                             <p 
                               className="text-sm mt-1 italic"
                               style={{
                                 fontFamily: entry.fontFamily || insideFont || "'Inter', sans-serif",
                                 color: entry.fill || insideFill || "#000000",
                                 opacity: 0.8,
                               }}
                             >
                               — {entry.signature}
                             </p>
                           )}
                         </div>
                         <button
                           onClick={() => setInsideTextEntries(prev => prev.filter(e => e.id !== entry.id))}
                           className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                           title="Remove this message"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       </div>
                     ))}
                   </div>
                   <p className="text-xs text-purple-600 mt-3">
                     💡 These messages are from collaborators. Drag on canvas to reposition, or delete if needed.
                   </p>
                 </div>
               )}
   
{/* primary actions */}
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 justify-center">
                {/* Hide AI Edits button for video templates */}
                {mediaType !== "video" && (
                  <Button
                    onClick={() => setAiPanelOpen((p) => !p)}
                    variant="primary"
                    className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full hover:from-blue-700 hover:to-blue-800 transition-all text-sm sm:text-base w-full sm:w-auto"
                  >
                    <Wand2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                    <span className="hidden sm:inline">{aiPanelOpen ? "Close AI Panel" : "Apply AI Edits"}</span>
                    <span className="sm:hidden">{aiPanelOpen ? "Close AI" : "AI Edit"}</span>
                  </Button>
                )}
                
                {/* Undo/Redo buttons - only for image templates */}
                {mediaType !== "video" && stateHistory.length > 0 && (
                  <div className="flex gap-1 sm:gap-2">
                    <Button
                      onClick={handleUndo}
                      disabled={!canUndo}
                      variant="secondary"
                      className={`flex items-center justify-center gap-1 px-3 sm:px-4 py-2.5 rounded-full transition-all text-sm ${
                        canUndo 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      }`}
                      title="Undo last AI edit"
                    >
                      <Undo2 size={16} />
                      <span className="hidden sm:inline">Undo</span>
                    </Button>
                    <Button
                      onClick={handleRedo}
                      disabled={!canRedo}
                      variant="secondary"
                      className={`flex items-center justify-center gap-1 px-3 sm:px-4 py-2.5 rounded-full transition-all text-sm ${
                        canRedo 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      }`}
                      title="Redo AI edit"
                    >
                      <Redo2 size={16} />
                      <span className="hidden sm:inline">Redo</span>
                    </Button>
                  </div>
                )}
   
                 {/* Extend background */}
                 {false && tab === "cover" && (
                   <Button
                     onClick={extendToCanvas}
                     disabled={extendLoading}
                     variant="secondary"
                     className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full hover:from-purple-200 hover:to-pink-200 transition-all text-sm sm:text-base w-full sm:w-auto"
                   >
                     <Maximize2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                     <span className="hidden sm:inline">
                       {extendLoading ? "Extending Background…" : "Extend Background"}
                     </span>
                     <span className="sm:hidden">{extendLoading ? "Extending…" : "Extend BG"}</span>
                   </Button>
                 )}
   
                 <Button
                   id="save-section"
                   onClick={handleManualSave}
                   disabled={saving}
                   variant="secondary"
                   className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 rounded-full transition-all text-sm sm:text-base w-full sm:w-auto ${
                     saveStatus === 'saving' 
                       ? 'bg-orange-100 text-orange-700 border border-orange-300' 
                       : saveStatus === 'saved' && !hasUnsavedChanges
                       ? 'bg-green-100 text-green-700 border border-green-300' 
                       : saveStatus === 'error'
                       ? 'bg-red-100 text-red-700 border border-red-300'
                       : hasUnsavedChanges
                       ? 'bg-blue-100 text-blue-700 border border-blue-300'
                       : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                   }`}
                 >
                   {saveStatus === 'saving' ? (
                     <>
                       <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-t-transparent"></div>
                       <span className="hidden sm:inline">Saving...</span>
                       <span className="sm:hidden">...</span>
                     </>
                   ) : saveStatus === 'saved' && !hasUnsavedChanges ? (
                     <>
                       <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                       </svg>
                       <span className="hidden sm:inline">Saved</span>
                       <span className="sm:hidden">✓</span>
                     </>
                   ) : saveStatus === 'error' ? (
                     <>
                       <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                       </svg>
                       <span className="hidden sm:inline">Retry Save</span>
                       <span className="sm:hidden">Retry</span>
                     </>
                   ) : hasUnsavedChanges ? (
                     <>
                       <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                       <span className="hidden sm:inline">Save Changes</span>
                       <span className="sm:hidden">Save</span>
                     </>
                   ) : (
                     <>
                       <RotateCcw size={16} className="sm:w-[18px] sm:h-[18px]" /> 
                       <span className="hidden sm:inline">Save Draft</span>
                       <span className="sm:hidden">Save</span>
                     </>
                   )}
                 </Button>
   
                                <Button
                 onClick={() => setShowPreview(true)}
                 variant="secondary"
                 className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full hover:from-gray-200 hover:to-gray-300 transition-all text-sm sm:text-base w-full sm:w-auto"
               >
                 <Eye size={16} className="sm:w-[18px] sm:h-[18px]" /> 
                 <span>Preview</span>
               </Button>
               </div>
   
              {/* inline AI panel - only for image templates */}
              {aiPanelOpen && mediaType !== "video" && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg space-y-4">
                  {/* Collapsed state when loading */}
                  {aiLoading ? (
                    <div className="flex items-center justify-center gap-3 py-2">
                      <div className="relative">
                        <div className="w-5 h-5 border-2 border-purple-200 rounded-full"></div>
                        <div className="absolute inset-0 w-5 h-5 border-2 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                      </div>
                      <span className="text-sm font-medium text-purple-700">
                        {photoSwapMode ? "Photo swap in progress..." : "AI edit in progress..."}
                      </span>
                      <span className="text-xs text-gray-500">
                        See progress above ↑
                      </span>
                    </div>
                  ) : (
                    <>
                  {/* Mode Toggle */}
                  <div className="flex rounded-lg bg-gray-200 p-1">
                    <button
                      onClick={() => {
                        setPhotoSwapMode(false);
                        clearReferenceImage();
                      }}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                        !photoSwapMode 
                          ? 'bg-white text-blue-700 shadow-sm' 
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <Wand2 size={14} className="inline mr-1.5" />
                      AI Edit
                    </button>
                    <button
                      onClick={() => {
                        setPhotoSwapMode(true);
                      }}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                        photoSwapMode 
                          ? 'bg-white text-purple-700 shadow-sm' 
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <RefreshCw size={14} className="inline mr-1.5" />
                      Photo Swap
                    </button>
                  </div>

                  {/* Photo Swap Mode - Reference Image Upload */}
                  {photoSwapMode && (
                    <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                          <Upload size={16} />
                          Reference Image
                        </h3>
                        {referencePreview && (
                          <button 
                            onClick={clearReferenceImage}
                            className="text-gray-400 hover:text-red-500 transition"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-purple-600">
                        Upload the image with the face/subject you want to swap into your card
                      </p>
                      
                      {referencePreview ? (
                        <div 
                          className="relative rounded-lg overflow-hidden bg-gray-100 border border-purple-200"
                          onDrop={handleReferenceDrop}
                          onDragOver={handleReferenceDragOver}
                          onDragLeave={handleReferenceDragLeave}
                        >
                          <img 
                            src={referencePreview} 
                            alt="Reference" 
                            className="w-full h-32 object-contain"
                          />
                        </div>
                      ) : (
                        <label 
                          className="block cursor-pointer"
                          onDrop={handleReferenceDrop}
                          onDragOver={handleReferenceDragOver}
                          onDragLeave={handleReferenceDragLeave}
                        >
                          <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                            isDraggingRef 
                              ? 'border-purple-500 bg-purple-100 scale-[1.02]' 
                              : 'border-purple-300 hover:border-purple-400 hover:bg-purple-50'
                          }`}>
                            <Upload className={`w-8 h-8 mx-auto mb-2 transition-colors ${
                              isDraggingRef ? 'text-purple-600' : 'text-purple-400'
                            }`} />
                            <p className="text-purple-600 font-medium text-sm">
                              {isDraggingRef ? 'Drop image here!' : 'Click or drag & drop'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">Your pet, person, etc.</p>
                          </div>
                          <input
                            ref={referenceInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleReferenceUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      {photoSwapMode ? "Describe the swap" : "Edit prompt"}
                    </label>
                    {photoSwapMode && referencePreview && (
                      <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                        ✓ Reference ready
                      </span>
                    )}
                  </div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    placeholder={photoSwapMode
                      ? "Describe what you want to swap (e.g., 'Replace the face with my pet's face', 'Swap the character with my photo')"
                      : "Describe how you want to transform the image (e.g., 'add festive confetti around the character', 'change to evening lighting')"
                    }
                  />
                  
                  {/* Mode-specific info */}
                  <div className="space-y-2">
                    {photoSwapMode ? (
                      <>
                        <div className="text-sm text-purple-600 bg-purple-50 p-2 rounded">
                          🔄 <strong>Photo Swap mode:</strong> Upload a reference image and describe what you want to swap. AI will seamlessly blend your reference into the card.
                        </div>
                        <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          <div className="font-medium mb-1">💡 Tips for best results:</div>
                          <ul className="space-y-1">
                            <li>• <strong>"Fully replace the face with..."</strong></li>
                            <li>• <strong>"Completely swap the head with..."</strong></li>
                            <li>• <strong>"Remove the person and put my pet in their place"</strong></li>
                          </ul>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                        🖼️ <strong>AI Edit mode:</strong> Describe how you want to transform the image. AI will apply changes while maintaining the overall style.
                      </div>
                    )}
                  </div>
                    
                   <div className="flex gap-2">
                    <Button
                      onClick={photoSwapMode ? runPhotoSwap : runAiEdit}
                      disabled={aiLoading || (photoSwapMode && !referenceImage)}
                      variant="primary"
                      className={`flex-1 flex items-center justify-center gap-2 ${
                        photoSwapMode 
                          ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700' 
                          : ''
                      }`}
                    >
                      {photoSwapMode ? (
                        <>
                          <RefreshCw size={16} />
                          Swap Photo
                        </>
                      ) : "Apply AI Edit"}
                    </Button>
                    <Button
                      onClick={() => {
                        setAiPanelOpen(false);
                        setPrompt("");
                        setAiError("");
                        setPhotoSwapMode(false);
                        clearReferenceImage();
                      }}
                      variant="secondary"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                   {aiError && (
                     <div className="bg-red-50 border border-red-200 rounded p-3">
                       <p className="text-red-600 text-sm">{aiError}</p>
                       <p className="text-red-500 text-xs mt-1">
                         Try: simpler prompts or rephrasing your request
                       </p>
                     </div>
                   )}
                   </>
                  )}
                 </div>
               )}
   
               {/* inside page navigation if inside tab */}
               {tab === "inside" && (
                 <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                   <div className="flex flex-wrap gap-2">
                     {insidePages.map((_, index) => (
                       <button
                         key={index}
                         onClick={() => setCurrentInsidePage(index)}
                         className={`px-3 sm:px-4 py-2 rounded text-sm sm:text-base ${
                           currentInsidePage === index
                             ? "bg-blue-600 text-white"
                             : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                         }`}
                       >
                         Page {index + 1}
                       </button>
                     ))}
                     <button
                       onClick={handleAddPage}
                       className="px-3 sm:px-4 py-2 rounded bg-green-100 text-green-700 hover:bg-green-200 text-sm sm:text-base whitespace-nowrap"
                     >
                       + Add Page
                     </button>
                   </div>
                   {insidePages.length > 1 && (
                     <button
                       onClick={() => handleRemovePage(currentInsidePage)}
                       className="w-full sm:w-auto px-3 sm:px-4 py-2 rounded bg-red-100 text-red-700 hover:bg-red-200 text-sm sm:text-base whitespace-nowrap mt-2 sm:mt-0"
                     >
                       Remove Current Page
                     </button>
                   )}
                 </div>
               )}
             </div>
   
             {/* right column – controls panel */}
             <div className="lg:col-span-5 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-2">
               <div className="bg-white rounded-lg shadow-md p-3 sm:p-6 lg:min-h-full">
                 <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
                   <button
                     onClick={() => setTab("cover")}
                     className={`flex-1 py-2 text-center rounded-lg transition-all text-sm sm:text-base ${
                       tab === "cover"
                         ? "border-b-2 border-blue-600 bg-blue-50/50 text-blue-600"
                         : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                     }`}
                   >
                     🎨 Cover
                   </button>
                   <button
                     onClick={() => setTab("inside")}
                     className={`flex-1 py-2 text-center rounded-lg transition-all text-sm sm:text-base ${
                       tab === "inside"
                         ? "bg-blue-600 text-white"
                         : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                     }`}
                   >
                     ✍️ Inside
                   </button>
                 </div>
   
                 <ControlsPanel
                   tab={tab}
                   layout={layout}
                   setLayout={setLayout}
                   frontText={frontText}
                   setFrontText={setFrontText}
                   insideText={insidePages[currentInsidePage]}
                   setInsideText={(t) => updateInsidePage(t)}
                   insideFont={insideFont}
                   setInsideFont={setInsideFont}
                   insideFontSize={insideFontSize}
                   setInsideFontSize={setInsideFontSize}
                   insideFill={insideFill}
                   setInsideFill={setInsideFill}
                   insideBorderColor={insideBorderColor}
                   setInsideBorderColor={setInsideBorderColor}
                   insideBorderWidth={insideBorderWidth}
                   setInsideBorderWidth={setInsideBorderWidth}
                   insideBorderStyle={insideBorderStyle}
                   setInsideBorderStyle={setInsideBorderStyle}
                   coverBorderColor={coverBorderColor}
                   setCoverBorderColor={setCoverBorderColor}
                   coverBorderWidth={coverBorderWidth}
                   setCoverBorderWidth={setCoverBorderWidth}
                   coverBorderStyle={coverBorderStyle}
                   setCoverBorderStyle={setCoverBorderStyle}
                   coverOverlay={coverOverlay}
                   setCoverOverlay={setCoverOverlay}
                   coverOverlayColor={coverOverlayColor}
                   setCoverOverlayColor={setCoverOverlayColor}
                   mediaType={mediaType}
                   signatures={signatures}
                   addSignature={(name) =>
                     setSignatures((s) => [...s, name.trim()])
                   }
                   removeSignature={(i) =>
                     setSignatures((s) => s.filter((_, idx) => idx !== i))
                   }
                   sloganTone={sloganTone}
                   setSloganTone={setSloganTone}
                   sloganRecipient={sloganRecipient}
                   setSloganRecipient={setSloganRecipient}
                   sloganOccasion={sloganOccasion}
                   setSloganOccasion={setSloganOccasion}
                   generateSlogan={generateAIHeadline}
                   loadingSlogan={loadingSlogan}
                 />
   
                 {/* share panel */}
                 <div id="collaboration-section" className="mt-8 space-y-2 bg-gray-50 p-4 rounded-lg">
                   <div className="flex items-center gap-2 mb-2">
                     <h3 className="font-semibold text-gray-800">
                       Share and Collaborate
                     </h3>
                     <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                       Step 1
                     </span>
                   </div>
                   <p className="text-sm text-gray-600 mb-4">
                     Set up collaboration details. The recipient name will automatically sync with the scheduling section below.
                   </p>
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm text-gray-600 mb-1">
                         Your name (required)
                       </label>
                       <input
                         id="sender-name-input"
                         type="text"
                         placeholder="Your name"
                         value={senderName}
                         onChange={(e) => setSenderName(e.target.value)}
                         className={`w-full rounded-lg border px-3 py-2 ${
                           senderName ? 'border-green-300 bg-green-50' : ''
                         }`}
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         📝 This will appear in emails as the sender
                       </p>
                     </div>
                     
                     <div>
                       <label className="block text-sm text-gray-600 mb-1">
                         Recipient name (who is this card for?)
                       </label>
                       <input
                         id="recipient-name-input"
                         type="text"
                         placeholder="Recipient name (ex. Sarah)"
                         value={recipientName}
                         onChange={(e) => updateRecipientName(e.target.value)}
                         className={`w-full rounded-lg border px-3 py-2 ${
                           recipientName ? 'border-green-300 bg-green-50' : ''
                         }`}
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         🎁 The person who will receive this card • 🔗 Syncs with schedule section
                       </p>
                     </div>
                     
                     <div>
                       <label className="block text-sm text-gray-600 mb-1">
                         Invite someone to edit this card
                       </label>
                       <div className="flex gap-2">
                         <input
                           id="invite-email-input"
                           type="email"
                           placeholder="collaborator@example.com"
                           value={inviteEmail}
                           onChange={(e) => setInviteEmail(e.target.value)}
                           className={`flex-1 rounded-lg border px-3 py-2 ${
                             inviteEmail ? 'border-green-300 bg-green-50' : ''
                           }`}
                         />
                         <Button
                           onClick={handleInvite}
                           disabled={inviteLoading || !cardId}
                           className="flex items-center gap-1"
                         >
                           <UserPlus size={16} />{" "}
                           {inviteLoading ? "Sending..." : "Invite"}
                         </Button>
                       </div>
                       <div>
                         <p className="text-xs text-gray-500 mt-1">
                           👥 Send a collaboration link to edit this card together
                         </p>
                       </div>
                       {!cardId && (
                         <p className="text-sm text-orange-600">
                           💾 Save the card first to enable sharing
                         </p>
                       )}
                       {inviteSuccess && (
                         <p className="text-sm text-green-600">✅ {inviteSuccess}</p>
                       )}
                       {inviteError && (
                         <div className={`mt-2 p-4 rounded-lg border ${inviteError.isPreviewLimit ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200' : 'bg-red-50 border-red-200'}`}>
                           <p className={`text-base font-medium mb-1 ${inviteError.isPreviewLimit ? 'text-slate-800' : 'text-red-700'}`}>
                             {inviteError.isPreviewLimit ? '' : '❌ '}{inviteError.message}
                           </p>
                           {inviteError.action && (
                             <p className={`text-sm mb-2 ${inviteError.isPreviewLimit ? 'text-slate-600' : 'text-red-600'}`}>
                               {inviteError.action}
                             </p>
                           )}
                           {inviteError.allErrors && inviteError.allErrors.length > 1 && (
                             <div className="mt-2 mb-3">
                               <p className="text-xs text-red-600 font-medium">All issues to fix:</p>
                               <ul className="text-xs text-red-600 mt-1 space-y-1">
                                 {inviteError.allErrors.map((error, index) => (
                                   <li key={index} className="flex items-start">
                                     <span className="mr-1">•</span>
                                     <span>{error.message}</span>
                                   </li>
                                 ))}
                               </ul>
                             </div>
                           )}
                           {inviteError.showUpgrade && (
                             <div className="flex flex-col sm:flex-row gap-2 mt-3">
                               <button
                                 onClick={() => navigate('/subscribe', {
                                   state: {
                                     fromFeature: 'collaboration',
                                     message: 'Upgrade to invite collaborators and work together on your cards!'
                                   }
                                 })}
                                 className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md"
                               >
                                 🚀 Upgrade Now
                               </button>
                               <button
                                 onClick={() => setInviteError("")}
                                 className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all"
                               >
                                 Dismiss
                               </button>
                             </div>
                           )}
                           {!inviteError.showUpgrade && (
                             <button
                               onClick={() => setInviteError("")}
                               className="mt-2 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-all"
                             >
                               Dismiss
                             </button>
                           )}
                         </div>
                       )}
                     </div>
                   </div>
                 </div>
   
                 {/* schedule panel */}
                 <div id="schedule-section" className="mt-8 space-y-2 bg-gray-50 p-4 rounded-lg">
                   <div className="flex items-center gap-2 mb-2">
                     <h3 className="font-semibold text-gray-800">
                       Schedule Delivery
                     </h3>
                     <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                       Step 2
                     </span>
                   </div>
                   <p className="text-sm text-gray-600 mb-4">
                     Choose when to automatically send your card. The recipient name syncs with the collaboration section above.
                   </p>
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm text-gray-600 mb-1">
                         Recipient information
                       </label>
                       <div className="space-y-2">
                         <input
                           id="recipient-email-input"
                           type="email"
                           placeholder="recipient@example.com"
                           value={recipientEmail}
                           onChange={(e) => setRecipientEmail(e.target.value)}
                           className={`w-full rounded-lg border px-3 py-2 ${
                             recipientEmail ? 'border-green-300 bg-green-50' : ''
                           }`}
                         />
                         <input
                           id="recipient-name-schedule-input"
                           type="text"
                           placeholder="e.g., Sarah, Mom, John"
                           value={recipientName}
                           onChange={(e) => updateRecipientName(e.target.value)}
                           className={`w-full rounded-lg border px-3 py-2 ${
                             recipientName ? 'border-green-300 bg-green-50' : ''
                           }`}
                         />
                       </div>
                       <p className="text-xs text-gray-500 mt-1">
                         📧 Where to send the card and who it's for • 🔗 Syncs with collaborate section
                       </p>
                     </div>
                     <div>
                       <label className="block text-sm text-gray-600 mb-1">
                         Select when to send this card
                       </label>
                       <p className="text-xs text-gray-500 mb-1">⏰ Timezone: {userTimeZone}</p>
                       <div className="flex gap-2">
                         <input
                           id="send-date-input"
                           type="datetime-local"
                           value={sendDate}
                           onChange={(e) => setSendDate(e.target.value)}
                           min={new Date().toISOString().slice(0, 16)}
                           className="flex-1 rounded-lg border px-3 py-2"
                         />
                         <Button
                           onClick={handleSchedule}
                           disabled={scheduleLoading || !cardId}
                           className="flex items-center gap-1"
                         >
                           <Calendar size={16} />{" "}
                           {scheduleLoading ? "Scheduling..." : "Schedule"}
                         </Button>
                       </div>
                       <p className="text-xs text-gray-500 mt-1">
                         🚀 Your card will be automatically sent at this time
                       </p>
                     </div>
                     {!cardId && (
                       <p className="text-sm text-orange-600">
                         💾 Save the card first to enable scheduling
                       </p>
                     )}
                                            {scheduleSuccess && (
                         <p className="text-sm text-green-600">✅ {scheduleSuccess}</p>
                       )}
                       {scheduleError && (
                         <div className={`mt-2 p-4 rounded-lg border ${scheduleError.isPreviewLimit ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200' : 'bg-red-50 border-red-200'}`}>
                           <p className={`text-base font-medium mb-1 ${scheduleError.isPreviewLimit ? 'text-slate-800' : 'text-red-700'}`}>
                             {scheduleError.isPreviewLimit ? '' : '❌ '}{scheduleError.message}
                           </p>
                           {scheduleError.action && (
                             <p className={`text-sm ${scheduleError.isPreviewLimit ? 'text-slate-600' : 'text-red-600'}`}>
                               {scheduleError.action}
                             </p>
                           )}
                           {scheduleError.allErrors && scheduleError.allErrors.length > 1 && (
                             <div className="mt-2">
                               <p className="text-xs text-red-600 font-medium">All issues to fix:</p>
                               <ul className="text-xs text-red-600 mt-1 space-y-1">
                                 {scheduleError.allErrors.map((error, index) => (
                                   <li key={index} className="flex items-start">
                                     <span className="mr-1">•</span>
                                     <span>{error.message}</span>
                                   </li>
                                 ))}
                               </ul>
                             </div>
                           )}
                           {scheduleError.showUpgrade && (
                             <div className="flex flex-col sm:flex-row gap-2 mt-3">
                               <button
                                 onClick={() => navigate('/subscribe', {
                                   state: {
                                     fromFeature: 'send',
                                     message: 'Upgrade to send your card!'
                                   }
                                 })}
                                 className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md"
                               >
                                 🚀 Upgrade Now
                               </button>
                               <button
                                 onClick={() => setScheduleError("")}
                                 className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all"
                               >
                                 Dismiss
                               </button>
                             </div>
                           )}
                           {!scheduleError.showUpgrade && (
                             <button
                               onClick={() => setScheduleError("")}
                               className="mt-2 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-all"
                             >
                               Dismiss
                             </button>
                           )}
                         </div>
                       )}
                     </div>
                   </div>
                 </div>
             </div>
           </div>
         </div>
   
         {/* preview modal */}
         {showPreview && (
           <PreviewModal
             cardState={previewCardState}
             onClose={() => setShowPreview(false)}
           />
         )}
   
         {/* save draft modal */}
         <SaveDraftModal
           isOpen={showSaveModal}
           onClose={() => setShowSaveModal(false)}
           onSave={handleSaveConfirm}
           defaultTitle={title || `Card for ${sloganRecipient}`}
         />
       </div>
     );
   }
   
   /* ===========================================================
      Reusable sub components
      ========================================================== */
   function SelectBox({ label, value, onChange, options }) {
     return (
       <div className="space-y-2">
         <label className="text-sm font-medium text-gray-700">{label}</label>
         <div className="relative">
           <select
             value={value}
             onChange={onChange}
             className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 appearance-none"
           >
             {options.map((o) => (
               <option key={o}>{o}</option>
             ))}
           </select>
           <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
         </div>
       </div>
     );
   }
   
   function RangeInput({ label, value, min, max, onChange, suffix = "" }) {
     return (
       <div className="space-y-2">
         <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
           {label}
         </label>
         <div className="rounded-lg bg-gray-50 px-3 py-2">
           <input
             type="range"
             min={min}
             max={max}
             value={value}
             onChange={(e) => onChange(+e.target.value)}
             className="w-full"
           />
           <div className="mt-1 text-center text-xs text-gray-500">
             {value}
             {suffix}
           </div>
         </div>
       </div>
     );
   }
   
   function HeadlineEditor({ text, setText }) {
     return (
       <div className="space-y-3">
         <label className="flex items-center gap-2 font-semibold text-gray-800">
           <Type className="h-4 w-4" /> Headline
         </label>
         <textarea
           rows={3}
           value={text}
           onChange={(e) => setText(e.target.value)}
           className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-500"
           placeholder="Enter your headline text…"
         />
       </div>
     );
   }
   
   function SignTab({
    inside,
    setInside,
    insideFont,
    setInsideFont,
    insideFontSize,
    setInsideFontSize,
    insideFill,
    setInsideFill,
    insideBorderColor,
    setInsideBorderColor,
    insideBorderWidth,
    setInsideBorderWidth,
    insideBorderStyle,
    setInsideBorderStyle,
    signatures,
    addSignature,
    removeSignature,
  }) {
    // Border style options with visual previews
    const borderStyles = [
      { id: 'solid', name: 'Solid', icon: '▬' },
      { id: 'double', name: 'Double', icon: '═' },
      { id: 'dashed', name: 'Dashed', icon: '┅' },
    ];
     return (
       <div className="space-y-6">
         <div className="space-y-3">
           <label className="font-semibold text-gray-800">Inside Message</label>
           <textarea
             rows={8}
             value={inside}
             onChange={(e) => setInside(e.target.value)}
             className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-500"
             placeholder="Write your heartfelt message here…"
           />
         </div>

         {/* Canvas Instructions */}
         <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
           <div className="flex items-center gap-2 mb-2">
             <Type className="h-4 w-4 text-blue-600" />
             <h3 className="font-semibold text-blue-700">Canvas Editing</h3>
           </div>
           <div className="text-sm space-y-1 text-blue-700">
             <p>• <strong>Drag</strong> text on the canvas to position it</p>
             <p>• <strong>Double-click</strong> text on the canvas to edit it</p>
             <p>• Use the textarea above or canvas editing - both work!</p>
           </div>
         </div>

         {/* Font Controls */}
         <SelectBox
           label="Font Style"
           value={insideFont}
           onChange={(e) => setInsideFont(e.target.value)}
           options={[
             "'Inter', sans-serif",
             "'Georgia', serif",
             "'Cedarville Cursive', cursive",
             "'Patrick Hand', cursive",
           ]}
         />

         <RangeInput
           label="Font Size"
           value={insideFontSize}
           min={12}
           max={60}
           onChange={setInsideFontSize}
           suffix="px"
         />

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Text Color</label>
          <div className="flex items-center gap-2">
            {[
              "#000000",
              "#EF4444",
              "#10B981",
              "#3B82F6",
              "#FBBF24",
            ].map((c) => (
              <button
                key={c}
                onClick={() => setInsideFill(c)}
                style={{ backgroundColor: c }}
                className={`h-8 w-8 rounded-full border-2 transition ${
                  insideFill === c
                    ? "border-blue-600 ring-2 ring-blue-100"
                    : "border-gray-200"
                }`}
              />
            ))}
            <input
              type="color"
              value={insideFill}
              onChange={(e) => setInsideFill(e.target.value)}
              className="h-8 w-8 rounded-xl border border-gray-200 cursor-pointer"
            />
          </div>
        </div>

        {/* Inside Card Design - Decorative Border */}
        <div className="space-y-4 bg-purple-50 rounded-xl p-4 border border-purple-200">
          <h3 className="font-semibold text-purple-700 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
            </svg>
            Card Border Design
          </h3>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Border Color</label>
            <div className="flex flex-wrap items-center gap-2">
              {/* No border option */}
              <button
                onClick={() => { setInsideBorderColor(""); setInsideBorderWidth(0); }}
                className={`h-8 w-8 rounded-full border-2 transition flex items-center justify-center ${
                  !insideBorderColor
                    ? "border-purple-600 ring-2 ring-purple-100 bg-white"
                    : "border-gray-200 bg-gray-100"
                }`}
                title="No border"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {/* Preset border colors - elegant card-like colors */}
              {[
                "#7C3AED", // Purple (like the example image)
                "#831843", // Maroon/Wine
                "#1E40AF", // Navy Blue
                "#065F46", // Forest Green
                "#92400E", // Bronze
                "#DC2626", // Classic Red
                "#0F766E", // Teal
                "#4338CA", // Indigo
              ].map((c) => (
                <button
                  key={c}
                  onClick={() => { 
                    setInsideBorderColor(c); 
                    if (insideBorderWidth === 0) setInsideBorderWidth(20);
                  }}
                  style={{ backgroundColor: c }}
                  className={`h-8 w-8 rounded-full border-2 transition ${
                    insideBorderColor === c
                      ? "border-purple-600 ring-2 ring-purple-100"
                      : "border-gray-200"
                  }`}
                />
              ))}
              <input
                type="color"
                value={insideBorderColor || "#7C3AED"}
                onChange={(e) => {
                  setInsideBorderColor(e.target.value);
                  if (insideBorderWidth === 0) setInsideBorderWidth(20);
                }}
                className="h-8 w-8 rounded-xl border border-gray-200 cursor-pointer"
              />
            </div>
          </div>

          {/* Border Width - only show when a color is selected */}
          {insideBorderColor && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Border Width: {insideBorderWidth}px
                </label>
                <input
                  type="range"
                  min={8}
                  max={40}
                  value={insideBorderWidth}
                  onChange={(e) => setInsideBorderWidth(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Thin</span>
                  <span>Thick</span>
                </div>
              </div>

              {/* Border Style selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Border Style</label>
                <div className="grid grid-cols-4 gap-2">
                  {borderStyles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setInsideBorderStyle(style.id)}
                      className={`relative p-2 rounded-lg border-2 transition-all ${
                        insideBorderStyle === style.id
                          ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                          : 'border-gray-200 bg-white hover:border-purple-300'
                      }`}
                    >
                      {/* Visual preview of border style */}
                      <div 
                        className="w-full h-8 rounded flex items-center justify-center mb-1"
                        style={{ 
                          border: style.id === 'solid' ? `3px solid ${insideBorderColor}` :
                                  style.id === 'double' ? `4px double ${insideBorderColor}` :
                                  style.id === 'dashed' ? `3px dashed ${insideBorderColor}` :
                                  'none',
                        }}
                      />
                      <span className="text-xs font-medium text-gray-600">{style.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="space-y-3">
          <label className="font-semibold text-gray-800">Signatures</label>
           <input
             type="text"
             placeholder="Add a signature…"
             className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-blue-500"
             onKeyDown={(e) => {
               if (e.key === "Enter") {
                 addSignature(e.target.value);
                 e.target.value = "";
               }
             }}
           />
           <div className="flex flex-wrap gap-2">
             {signatures.map((s, i) => (
               <span
                 key={i}
                 className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 px-3 py-2 text-sm font-medium text-blue-700"
               >
                 {s}
                 <button
                   onClick={() => removeSignature(i)}
                   className="text-blue-400 hover:text-blue-600"
                 >
                   ×
                 </button>
               </span>
             ))}
           </div>
         </div>
       </div>
     );
   }
   
     function ControlsPanel({
    tab,
    layout,
    setLayout,
    frontText,
    setFrontText,
    insideText,
    setInsideText,
    insideFont,
    setInsideFont,
    insideFontSize,
    setInsideFontSize,
    insideFill,
    setInsideFill,
    insideBorderColor,
    setInsideBorderColor,
    insideBorderWidth,
    setInsideBorderWidth,
    insideBorderStyle,
    setInsideBorderStyle,
    coverBorderColor,
    setCoverBorderColor,
    coverBorderWidth,
    setCoverBorderWidth,
    coverBorderStyle,
    setCoverBorderStyle,
    coverOverlay,
    setCoverOverlay,
    coverOverlayColor,
    setCoverOverlayColor,
    mediaType,
    signatures,
    addSignature,
    removeSignature,
    sloganTone,
    setSloganTone,
    sloganRecipient,
    setSloganRecipient,
    sloganOccasion,
    setSloganOccasion,
    generateSlogan,
    loadingSlogan,
  }) {
     const colourOptions = [
       "#FFFFFF",
       "#000000",
       "#EF4444",
       "#10B981",
       "#3B82F6",
       "#FBBF24",
       "#A855F7",
       "#EC4899",
     ];
   
         return (
      <div className="flex flex-col h-full rounded-2xl bg-white shadow-lg">
        <div className="flex-1 overflow-y-auto space-y-6 p-6">
          {tab === "cover" ? (
             <>
               {/* AI slogan */}
               <div className="space-y-4 bg-blue-50 rounded-xl p-4">
                 <h3 className="flex items-center gap-2 font-semibold text-blue-700">
                   <ThumbsUp className="h-4 w-4" /> AI Slogan Generator
                 </h3>
   
                 <div className="grid grid-cols-3 gap-4">
                   <SelectBox
                     label="Tone"
                     value={sloganTone}
                     onChange={(e) => setSloganTone(e.target.value)}
                     options={[
                       "Funny",
                       "Heartfelt",
                       "Formal",
                       "Casual",
                     ]}
                   />
                   <SelectBox
                     label="Recipient"
                     value={sloganRecipient}
                     onChange={(e) => setSloganRecipient(e.target.value)}
                     options={[
                       "Friend",
                       "Coworker",
                       "Parent",
                       "Partner",
                       "Sibling",
                       "Child",
                       "Teacher",
                       "Boss",
                     ]}
                   />
                   <SelectBox
                     label="Occasion"
                     value={sloganOccasion}
                     onChange={(e) => setSloganOccasion(e.target.value)}
                     options={[
                       "Graduation",
                       "Birthday",
                       "New Job",
                       "Anniversary",
                     ]}
                   />
                 </div>
   
                 <Button
                   onClick={generateSlogan}
                   disabled={loadingSlogan}
                   className="w-full bg-gradient-to-r from-purple-600 via-blue-600 to-teal-600 hover:from-purple-700 hover:via-blue-700 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden"
                 >
                   <div className="flex items-center justify-center gap-2 relative z-10">
                     {loadingSlogan ? (
                       <>
                         <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                         <span>Generating Magic...</span>
                       </>
                     ) : (
                       <>
                         <Wand2 className="h-5 w-5 animate-pulse" />
                         <span>✨ Generate AI Slogan</span>
                       </>
                     )}
                   </div>
                   {/* Animated background shimmer effect */}
                   <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                 </Button>
               </div>
   
               {/* headline text */}
               <HeadlineEditor text={frontText} setText={setFrontText} />
   
               {/* style controls */}
               <div className="space-y-4">
                 <h3 className="flex items-center gap-2 font-semibold text-gray-800">
                   <Edit2 className="h-4 w-4" /> Style Controls
                 </h3>
   
                 {/* font family */}
                 <div className="space-y-2">
                   <label className="text-sm font-medium text-gray-700">
                     Font Family
                   </label>
                   <FontPicker
                     value={layout.fontFamily}
                     onChange={(f) => setLayout({ ...layout, fontFamily: f })}
                   />
                 </div>
   
                 {/* text colour */}
                 <div className="space-y-2">
                   <label className="text-sm font-medium text-gray-700">
                     Text Color
                   </label>
                   <div className="flex items-center gap-2">
                     {colourOptions.map((c) => (
                       <button
                         key={c}
                         onClick={() => setLayout({ ...layout, fill: c })}
                         style={{ backgroundColor: c }}
                         className={`h-8 w-8 rounded-full border-2 transition ${
                           layout.fill === c
                             ? "border-blue-600 ring-2 ring-blue-100"
                             : "border-gray-200"
                         }`}
                       />
                     ))}
                     <input
                       type="color"
                       value={layout.fill}
                       onChange={(e) =>
                         setLayout({ ...layout, fill: e.target.value })
                       }
                       className="h-8 w-8 rounded-xl border border-gray-200 cursor-pointer"
                     />
                   </div>
                 </div>
   
                 {/* stroke */}
                 <div className="space-y-2">
                   <label className="text-sm font-medium text-gray-700">
                     Text Stroke
                   </label>
                   <div className="flex items-center gap-2">
                     {[
                       { label: "None", value: "" },
                       { label: "White", value: "#FFFFFF" },
                       { label: "Black", value: "#000000" },
                     ].map(({ label, value }) => (
                       <button
                         key={label}
                         onClick={() =>
                           setLayout({
                             ...layout,
                             stroke: value,
                             strokeWidth: value ? 2 : 0,
                           })
                         }
                         className={`px-4 py-2 rounded-xl border transition ${
                           layout.stroke === value
                             ? "bg-blue-50 border-blue-600 text-blue-600"
                             : "border-gray-200 text-gray-700 hover:bg-gray-50"
                         }`}
                       >
                         {label}
                       </button>
                     ))}
                   </div>
                 </div>
   
                 {/* ranges */}
                 <RangeInput
                   label="Font Size"
                   value={layout.fontSize}
                   min={12}
                   max={120}
                   onChange={(v) => setLayout({ ...layout, fontSize: v })}
                   suffix="px"
                 />
   
                 <RangeInput
                   label={
                     <>
                       <RotateCcw className="h-3 w-3" /> Rotation
                     </>
                   }
                   value={layout.rotation}
                   min={-20}
                   max={20}
                   onChange={(v) => setLayout({ ...layout, rotation: v })}
                   suffix="°"
                 />
   
                {/* animation */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Animation
                  </label>
                  <div className="flex items-center gap-2">
                    {["none", "bounce", "wiggle"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setLayout({ ...layout, animStyle: s })}
                        className={`px-4 py-2 rounded-xl border transition ${
                          layout.animStyle === s
                            ? "bg-blue-50 border-blue-600 text-blue-600"
                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Cover Image Frame/Border */}
              <div className="space-y-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Image Frame
                </h3>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Frame Color</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* No frame option */}
                    <button
                      onClick={() => { setCoverBorderColor(""); setCoverBorderWidth(0); }}
                      className={`h-8 w-8 rounded-full border-2 transition flex items-center justify-center ${
                        !coverBorderColor
                          ? "border-amber-600 ring-2 ring-amber-100 bg-white"
                          : "border-gray-200 bg-gray-100"
                      }`}
                      title="No frame"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    {/* Preset frame colors */}
                    {[
                      "#FFFFFF", // White
                      "#1F2937", // Dark Gray
                      "#92400E", // Bronze/Gold
                      "#7C3AED", // Purple
                      "#DC2626", // Red
                      "#065F46", // Forest Green
                      "#1E40AF", // Navy Blue
                    ].map((c) => (
                      <button
                        key={c}
                        onClick={() => { 
                          setCoverBorderColor(c); 
                          if (coverBorderWidth === 0) setCoverBorderWidth(12);
                        }}
                        style={{ backgroundColor: c, border: c === "#FFFFFF" ? "2px solid #d1d5db" : undefined }}
                        className={`h-8 w-8 rounded-full border-2 transition ${
                          coverBorderColor === c
                            ? "border-amber-600 ring-2 ring-amber-100"
                            : "border-gray-200"
                        }`}
                      />
                    ))}
                    <input
                      type="color"
                      value={coverBorderColor || "#92400E"}
                      onChange={(e) => {
                        setCoverBorderColor(e.target.value);
                        if (coverBorderWidth === 0) setCoverBorderWidth(12);
                      }}
                      className="h-8 w-8 rounded-xl border border-gray-200 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Frame controls - only show when a color is selected */}
                {coverBorderColor && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Frame Width: {coverBorderWidth}px
                      </label>
                      <input
                        type="range"
                        min={4}
                        max={30}
                        value={coverBorderWidth}
                        onChange={(e) => setCoverBorderWidth(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Thin</span>
                        <span>Thick</span>
                      </div>
                    </div>

                    {/* Frame Style selector */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Frame Style</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'solid', name: 'Solid' },
                          { id: 'double', name: 'Double' },
                          { id: 'dashed', name: 'Dashed' },
                        ].map((style) => (
                          <button
                            key={style.id}
                            onClick={() => setCoverBorderStyle(style.id)}
                            className={`relative p-2 rounded-lg border-2 transition-all ${
                              coverBorderStyle === style.id
                                ? 'border-amber-600 bg-amber-50 ring-2 ring-amber-200'
                                : 'border-gray-200 bg-white hover:border-amber-300'
                            }`}
                          >
                            {/* Visual preview of frame style */}
                            <div 
                              className="w-full h-8 rounded flex items-center justify-center mb-1"
                              style={{ 
                                border: style.id === 'solid' ? `3px solid ${coverBorderColor}` :
                                        style.id === 'double' ? `4px double ${coverBorderColor}` :
                                        style.id === 'dashed' ? `3px dashed ${coverBorderColor}` :
                                        'none',
                              }}
                            />
                            <span className="text-xs font-medium text-gray-600">{style.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Decorative Overlays - Only show for image cards, not video */}
              {mediaType !== "video" && (
              <div className="space-y-4 bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <h3 className="font-semibold text-emerald-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Decorative Overlay
                </h3>
                
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700">Overlay Style</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'none', name: 'None', emoji: '✕' },
                      { id: 'snow-splatter', name: 'Snow Splatter', emoji: '❄️' },
                      { id: 'stars', name: 'Stars', emoji: '✨' },
                    ].map((overlay) => (
                      <button
                        key={overlay.id}
                        onClick={() => setCoverOverlay(overlay.id)}
                        className={`p-2 rounded-lg border-2 transition-all text-center ${
                          coverOverlay === overlay.id
                            ? 'border-emerald-600 bg-emerald-100 ring-2 ring-emerald-200'
                            : 'border-gray-200 bg-white hover:border-emerald-300'
                        }`}
                      >
                        <div className="text-lg mb-1">{overlay.emoji}</div>
                        <span className="text-xs font-medium text-gray-600">{overlay.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overlay Color - only show when an overlay is selected */}
                {coverOverlay !== 'none' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Overlay Color</label>
                    <div className="flex flex-wrap items-center gap-2">
                      {[
                        "#1F2937", // Dark Gray
                        "#FFFFFF", // White
                        "#DC2626", // Red
                        "#065F46", // Forest Green
                        "#92400E", // Bronze/Gold
                        "#1E40AF", // Navy Blue
                        "#7C3AED", // Purple
                      ].map((c) => (
                        <button
                          key={c}
                          onClick={() => setCoverOverlayColor(c)}
                          style={{ backgroundColor: c, border: c === "#FFFFFF" ? "2px solid #d1d5db" : undefined }}
                          className={`h-8 w-8 rounded-full border-2 transition ${
                            coverOverlayColor === c
                              ? "border-emerald-600 ring-2 ring-emerald-100"
                              : "border-gray-200"
                          }`}
                        />
                      ))}
                      <input
                        type="color"
                        value={coverOverlayColor}
                        onChange={(e) => setCoverOverlayColor(e.target.value)}
                        className="h-8 w-8 rounded-xl border border-gray-200 cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          ) : tab === "inside" ? (
             <SignTab
               inside={insideText}
               setInside={setInsideText}
               insideFont={insideFont}
               setInsideFont={setInsideFont}
               insideFontSize={insideFontSize}
               setInsideFontSize={setInsideFontSize}
               insideFill={insideFill}
               setInsideFill={setInsideFill}
               insideBorderColor={insideBorderColor}
               setInsideBorderColor={setInsideBorderColor}
               insideBorderWidth={insideBorderWidth}
               setInsideBorderWidth={setInsideBorderWidth}
               insideBorderStyle={insideBorderStyle}
               setInsideBorderStyle={setInsideBorderStyle}
               signatures={signatures}
               addSignature={addSignature}
               removeSignature={removeSignature}
             />
           ) : null}
         </div>
       </div>
     );
   }
   